// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Select from 'react-select';
import Loading from 'react-loading';
import { toast } from 'react-toastify';
import renderHTML from 'react-render-html';
import CreatableSelect from 'react-select/creatable';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

import Editable from '../Editable';

import isEqual from 'lodash.isequal';

import api from '../../api';
import ContextMenu from '../ContextMenu';
import plural from '../../utilities/plural';
import inputKey from '../../utilities/inputKey';
import copyText from '../../utilities/copy';
import download from '../../utilities/downloadable';
import capitalize from '../../utilities/capitalize';
import formatDate from '../../utilities/formatDate';
import reconcileTA from '../../utilities/reconcileTA';
import getSourceIcon from '../../utilities/getSourceIcon';
import { encodeURL, decodeURL } from '../../utilities/url';
import rotateTable from '../../utilities/rotateTable'
import evaluateField from '../../utilities/evaluateField';
import { get } from '../../utilities/reconcileTA';

import '../../styles/meta.css';
import { ADMIN_EMAILS } from '../../utilities/staxAdmins';

// TODO @Naru - bring support for remaining field types
const OPTS_SIGN_TYPE = {
    textbox: "Textbox",
    // textarea: "Paragraph",
    date: "Custom Date",
    today: "Current Date",
    // checkbox: "Checkbox"
    signature: "Signature",
    signatureMouseOnly: "Signature (Mouse only)",
};

export default class Meta extends React.Component {

    state = {
        // Meta data
        editMeta: null,
        editParagraph: null,
        retagMetaField: null,
        metadata: [],
        metaChanged: false,
        showAllMeta: false,
        showAllFlags: false, 
        showFullHistory: false,
        showFullAutomate: false,
        showFullSplits: false,  
        showAdvanced: false,
        showRecipients: false,
        selectedMetaFieldIdx: null,
        sortMetaAscending: null,

        // Set meta/field for entire selection
        selectionMetaKey: "",
        selectionMetaValue: "",

        // Fillable form inputs
        editInput: null,
        editInputBounds: null,
        inputBound: {},
        trainingOverride: {},

        // Create a new table 
        createNewTable: false,  
        editNewTableBounds: false,
        newTableTitle: "",
        newTableRows: 2,
        newTableCols: 2, 

        // Document filters
        filters: {},

        // Stack keys for dropdown list
        keys: [],

        // Misc
        retryable: true,
        resumable: true,
    }

    labelMessageShown = false

    getActions = () => {

        return {

            // Field meta
            field: [
                {
                    icon: "action-upload.png",
                    title: "Upload file(s)",
                    tooltip: "Upload one or more files and attach this metadata. You can also just drag files from your computer into the stack.",
                    action: () => this.props.uploadFilesDirect(null, [{
                        key: decodeURL(this.props.match.params.field),
                        value: decodeURL(this.props.match.params.value)
                    }]),
                }
            ],

            // Stack meta
            stack: [
                {
                    icon: "action-create.png",
                    title: "Create a new stack",
                    tooltip: "Create a new stack either from scratch or using a pre-trained model.",
                    action: () => this.props.createStack(this.props.stack),
                    admin: false,
                    noPseudo: true
                },
                {
                    icon: "action-upload.png",
                    title: "Upload file(s)",
                    tooltip: "Upload one or more files into this stack. You can also just drag files from your computer into the stack.",
                    action: this.props.uploadFiles,
                    admin: false,
                    noPseudo: true
                },
                {
                    icon: "action-download.png",
                    title: "Export document list",
                    tooltip: "Export a CSV file with a list of documents including its name, source, receive date, and metadata.",
                    action: async () => {
                        await this.props.loading();
                        const res = await api.download("/stack/export?stackId=" + this.props.stack._id);
                        download(res, "Documents in " + this.props.stack.path.replace(/\//g, "_") + ".csv");
                        this.props.loaded();
                    }
                },
                {
                    icon: "action-retrain.png",
                    title: "Retrain auto sorter",
                    tooltip: "Upload or move files into this stack? Retrain the bots that sort your files in here for you.",
                    action: () => this.props.retrainClassifier(this.props.stack),
                    admin: false,
                    noPseudo: true,
                    disabled: () => {
                        return this.props.stack && this.props.stack.trainer && (this.props.stack.trainer.isTraining || this.props.stack.trainer.manualTrigger);
                    }
                },
                {
                    icon: "action-move.png",
                    title: "Move stack",
                    tooltip: "Move this stack into another location.",
                    action: this.props.moveStack,
                    admin: false,
                    noPseudo: true
                }
            ],
        
            // Single document selected
            single: [
                {
                    title: (sel, doc) => doc && doc.urgent ? "Mark not urgent" : "Mark as urgent",
                    icon: "action-urgent.png",
                    tooltip: "Urgent documents will show up in Quick Access ▸ High Priority",
                    action: (sel, doc) => this.props.updateDocument(doc._id, { urgent: !doc.urgent })
                },
                {
                    title: (sel, doc) => doc && doc.reviewed ? "Mark unread" : "Mark as read",
                    icon: "action-read.png",
                    tooltip: "Unread documents will show up in Quick Access ▸ Not Yet Reviewed",
                    action: (sel, doc) => this.props.updateDocument(doc._id, { reviewed: !doc.reviewed })
                },
                {
                    title: "Download File",
                    icon: "action-download.png",
                    tooltip: "Download document to view on your computer outside of Stax·ai",
                    action: (sel, doc) => this.props.downloadDocument(doc)
                },
                {
                    title: "Add Attachment",
                    icon: "action-attach.png",
                    tooltip: "Upload an attachment file to this document",
                    action: (sel, doc) => this.props.uploadAttachment(doc._id)
                },
                ...(this.props.document && (!this.props.document.format || this.props.document.format === "doc") ? [
                    {
                        title: "Print document",
                        icon: "action-print.png",
                        tooltip: "Send this document to a connected printer",
                        action: (sel, doc) => this.props.printDocument(doc)
                    }
                ] : []),
                {
                    title: "Share document",
                    icon: "action-share.png",
                    tooltip: "Share this document to one or more email addresses. To share this document within your Stax·ai team, just tag a user on a comment in the document.",
                    action: (sel, doc) => this.props.sendDocument(doc)
                },
                {
                    title: "Move document",
                    icon: "action-move.png",
                    tooltip: "Move this document into another stack",
                    action: (sel, doc) => this.props.moveDocuments([doc._id])
                },
                {
                    title: "Split document",
                    icon: "action-split.png",
                    tooltip: "Confirm document split by page splitters. Insert page splitters by clicking on space between document pages.",
                    action: (sel, doc) => this.props.splitDocument(doc)
                },
                {
                    title: <>Splice document <span className='new'>NEW</span></>,
                    icon: "action-split.png",
                    tooltip: "Select pages from this document you would like to use to create a new one from.",
                    action: (sel, doc) => {
                        // If this.props.function is already splice-document, then we just need to call it. 
                        if(this.props.function === "splice-document" && this.props.functionData?.pages?.length){
                            this.props.modal("splice-document", {
                                pages: this.props.functionData.pages, 
                            })
                        }else{
                            toast.info("Select pages from this document to create a new one from.");
                            this.props.setFunction("splice-document", {
                                pages: [], 
                            });
                        }
                    }
                },
                ...(window.location.href.includes("/document") ? [
                    {
                        title: "Add labeled box",
                        icon: "action-bound.png",
                        tooltip: "Insert an editable bounding box to label spatial information in your document.",
                        action: (sel, doc) => this.props.setDocumentFunction("draw-bounds")
                    }
                ] : []),
                ...(window.location.href.includes("/document") && this.props.document && (!this.props.document.fillable || !this.props.document.fillable.enabled) ? [
                    {
                        title: "Configure inputs",
                        icon: "action-fill.png",
                        tooltip: "Add form fields to make this into a sharable document that users can fill & sign",
                        action: (sel, doc) => this.props.updateDocument(doc._id, {
                            fillable: {
                                enabled: true,
                                fields: []
                            }
                        })
                    }
                ] : []),
                {
                    title: "Delete document",
                    icon: "delete.png",
                    tooltip: "Delete document.",
                    action: (sel, doc) => this.props.modal("confirm", {
                        message: <>You are about to delete this document. All other team users will lose access to the document as well. To restore this document, go to <a href="/trash">Trash</a>.</>,
                        action: async () => {
                            if(String(window.location.href).includes("document")){
                                this.props.loading(9999)
                                await this.props.removeDocuments([doc._id]);
                                window.location.href = "/stack/" + this.props.stack._id;
                                this.props.loaded()
                            }
                            else{
                                await this.props.removeDocuments([doc._id]);
                            }
                        }
                    })
                }
            ],
        
            // Multiple documents selected
            multiple: [
                {
                    title: "Mark all as urgent",
                    icon: "action-urgent.png",
                    tooltip: "Mark all of the selected documents as urgent",
                    action: (sel, doc) => this.props.updateDocuments(sel, { urgent: true })
                },
                {
                    title: "Mark all not urgent",
                    icon: "action-not-urgent.png",
                    tooltip: "Mark all of the selected documents as not urgent",
                    action: (sel, doc) => this.props.updateDocuments(sel, { urgent: false })
                },
                {
                    title: "Mark all as read",
                    icon: "action-read.png",
                    tooltip: "Mark all of the selected documents as read",
                    action: (sel, doc) => this.props.updateDocuments(sel, { reviewed: true })
                },
                {
                    title: "Mark all not read",
                    icon: "action-not-read.png",
                    tooltip: "Mark all of the selected documents as not read",
                    action: (sel, doc) => this.props.updateDocuments(sel, { reviewed: false })
                },
                {
                    title: "Move all",
                    icon: "action-move.png",
                    tooltip: "Move selected documents into a different stack",
                    action: (sel, doc) => this.props.moveDocuments(sel)
                },
                {
                    title: "Delete all",
                    icon: "delete.png",
                    tooltip: "Delete selected document.",
                    action: (sel, doc) => this.props.modal("confirm", {
                        message: <>You are about to delete <strong> {this.props.selection.length} </strong> selected documents. All other team users will lose access to these documents as well. To restore these documents, go to <a href="/trash">Trash</a>.</>,
                        action: async () => await this.props.removeDocuments(sel)
                    })
                }
            ]
        
        }
    }

    fetchStackKeys = async () => {
        const doc = window.location.href.includes("document") ? this.props.document : this.getSelectedDocument();
        const stack = this.props.stack;

        if (!doc || !doc._id || !doc.stack || !stack || !stack._id) return this.setState({ keys: [] });

        // Only fetch if this is a pseudo-stack
        if (stack._id.length === 24)
            return this.setState({
                keys: stack.keys && stack.keys.length ? stack.keys : []
            });

        // Let's fetch the stack from the API
        const remote = await api.get(`/stack/fields?stackId=${doc.stack}`);
        if (remote && remote.fields)
            this.setState({
                keys: remote.fields ?? []
            });
    }

    componentDidMount = async () => {
        // Single file upload dialog
        this.fileUploadDialog = document.createElement('input');
        this.fileUploadDialog.setAttribute('type', 'file');
    }
    
    componentWillReceiveProps = (next) => {

        // Listen for user tagged fields
        if (next && next.document && next.document.newTag && "key" in next.document.newTag && "value" in next.document.newTag) {
                this.fetchStackKeys(); // Synchronously

                // Set to edit mode and add new field
                this.setState({
                    editMeta: next.document._id,
                    editParagraph: null,
                    metadata: [
                        ...(this.state.editMeta === next.document._id ? 
                            this.state.metadata : next.document.metadata),
                        next.document.newTag
                    ]
                }, () => {
                    // Remove from MyStacks state so we can listen for new user tags
                    this.props.clearNewTag();
                })
            }
        
        // Metadata changed outside?
        if (!this.props.document || !this.props.document.metadata || !next.document || !next.document.metadata || this.state.editMeta !== this.props.document._id)
            return;

        if (!isEqual(next.document.metadata, this.props.document.metadata))
            this.reloadMetadata(next.document.metadata);
    }

    reloadMetadata = (src) => {
        let metadata = [];
        for (const idx in src)
            if (metadata.length <= idx)
                metadata.push({ ...src[idx] });
        this.setState({ metadata });
    }
    
    getPublicLink = (doc) => "https://my.stax.ai/public/" + doc._id;

    getSelectedDocument = () => {
        if (!this.props.selection || this.props.selection.length !== 1)
            return { _id: null, name: "" };
        const doc = this.props.docs.find(doc => doc._id === this.props.selection[0]);
        // if (!doc)
        //     this.props.select([]);
        return doc;
    }

    getStackPath = (stackId) => {
        const stack = this.props.stacks.find(s => s._id === stackId);
        return stack ? stack.path : "Unclassified";
    }

    getStackForms = (stackId) => {
        const stack = this.props.stacks.find(s => s._id === stackId);
        return stack ? stack.forms : [];
    }

    getUserName = (users, userId) => {
        const user = users.find(u => u._id === userId);
        if (!user) return "";
        return user.name;
    }

    getContextMenuOptions = (doc) => {
        let opts = [
            {
                label: 'Copy value',
                action: (meta) => {
                    copyText(meta.value);
                    toast.info("Copied to clipboard");
                }
            },
            {
                label: 'Filter by field',
                action: (meta) => window.location.href = '/field/' + encodeURL(meta.key) + '/' + encodeURL(meta.value)
            }
        ]

        if (this.state.selectedMetaFieldIdx !== null) {
            if (doc.metadata[this.state.selectedMetaFieldIdx]?.confidence < 1 ) {
                opts.push({
                    label: 'Accept value',
                    action: (meta) => {
                        let metadata = [...doc.metadata]
                        // If it has a confidence, set it to 100% 
                        if (metadata[meta.idx].confidence) {
                            metadata[meta.idx].confidence = 1;
                            this.props.updateDocument(doc._id, {
                                metadata
                            });
                        }
                    }
                })
            }

            // Flip sign?
            const val = doc.metadata[this.state.selectedMetaFieldIdx]?.value;
            if (val) {
                const num = parseFloat(val.replace(/[^0-9.]/g, '') ?? NaN);
                if (num !== 0 && !isNaN(num)) {
                    opts.push({
                        label: 'Flip sign',
                        action: (meta) => {
                            let metadata = [...doc.metadata];
                            if (val.includes('(') || val.includes(')') || val.includes('-'))
                                metadata[meta.idx].value = num.toLocaleString();
                            else
                                metadata[meta.idx].value = `(${num.toLocaleString()})`;
                            this.props.updateDocument(doc._id, {
                                metadata
                            });
                        }
                    })
                }
            }

            if ((doc.metadata[this.state.selectedMetaFieldIdx]?.pages?.length ?? 0) > 0) {
                opts.push({
                    label: 'Scroll to page',
                    action: (meta) => {
                        const page = doc.metadata[meta.idx].pages[0];
                            setTimeout(() => { // auto scroll to the page 
                                const pageDiv = document.getElementById('page-' + String(page));
                                if (pageDiv) pageDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 100);
                    }
                })
            }
            if (val) {
                const num = parseFloat(val.replace(/[^0-9.]/g, '') ?? NaN);
                if (num !== 0 && !isNaN(num)) {
                    opts.push({
                        label: 'Set reference',
                        action: (meta, x, y, event) => {
                            this.openRefMetaMenu(event, {meta});
                        }
                    });
                }
            }

        }
        return opts;
    }

    saveMetadata = async () => {
        if (this.state.editMeta && this.state.metaChanged) {
            for (const meta of this.state.metadata) {
                if (meta.key === "" )
                    return toast.error("Meta data key or value cannot be empty.");
            }
            this.props.updateDocument(this.state.editMeta, { metadata: this.state.metadata.map(f => ({
                ...f,
                key: f.key ? f.key.trim().toLowerCase() : "",
                value: f.value !== null ? String(f.expression ? evaluateField(this.state.metadata, f.key) : f.value) : f.value
            })) });
        }
        this.setState({
            editMeta: null,
            editParagraph: null,
            metaChanged: false,
            retagMetaField: null
        });
        this.props.setFunction();
    }

    setSelectionMeta = async () => {
        const key = this.state.selectionMetaKey.toLowerCase();
        const value = this.state.selectionMetaValue;

        if (key === "" || value === "")
            return toast.error("Field key or value is empty.");

        for (const docId of this.props.selection) {
            const doc = this.props.docs.find(d => d._id === docId);
            if (!doc)
                continue;

            const metadata = doc.metadata;
            
            // does this key exsit already? if so, update it
            const exsists = metadata.findIndex(e => e.key === key);
            if (exsists !== -1)
                metadata[exsists].value = value;
            else 
                metadata.push({
                    key, value
                })
            
            await this.props.updateDocument(docId, {
                metadata
            }, true, false);
        }

        this.setState({
            selectionMetaKey: "",
            selectionMetaValue: ""
        });
        toast.info("Set field for all selected documents.");
    }

    clearDocumentError = async () => {
        const doc = this.getSelectedDocument();
        if (!doc) return;
        await this.props.updateDocument(doc._id, {
            job: {
                ...doc.job,
                error: null
            }
        });
    }

    attachFile = () => {
        this.fileUploadDialog.click();
        this.fileUploadDialog.onchange = async (e) => {
            let path;
            if (e.path)
                path = e.path;
            else if (e.composedPath && e.composedPath())
                path = e.composedPath()
            else
                return; // raise toast error here?

            const doc = window.location.href.includes("document") ? this.props.document : this.getSelectedDocument();

            if (!doc || !doc._id || path.length === 0)
                return;

            if (path.length > 1)
                return toast.error("You can only attach one file at a time.");

            await this.props.loading(1005, "Uploading...");
            
            let formData = new FormData();
            formData.set('file', path[0].files[0]);
            const res = await api.post('/document/attach?docId=' + doc._id, formData);

            if (res && res.key && res.key.length > 10) {
                const pathCmp = res.key.split("/");
                const filename = pathCmp[pathCmp.length-1];
                let attachments = doc.attachments ? doc.attachments : [];
                attachments.push({
                    filename,
                    filepath: res.key
                });
                await this.props.updateDocument(doc._id, { attachments }, true);
            } else toast.error("Failed to attach file");

            this.props.loaded();
        }
    }

    renderLogEvent = (log, idx, user, team) => {
        if (log.event === "[AI] Input preprocessed" || log.event === "[AI] Context extracted")
            return ("");

        if (log.event === "[AI] Document Processed")
            log.event = "Processed";

        const userIdx = team.users.findIndex(u => u._id === log.user);

        let userName;
        if (log.event.substring(0,9) === "Filled by")
            userName = log.event.substring(10);
        else
            userName = userIdx >= 0 ? team.users[userIdx].name : "Stax·ai Bot";

        const offsetIdx = 8000 + userIdx - team.users.findIndex(u => u._id === user._id);
        
        return (<React.Fragment key={idx}>
            {idx > 0 && <div className="meta-automate-pipeline-pipe" />}
            <div className="meta-history-log" title={log.event + (userName ? ' by ' + userName : '')}>
                {userName === "Stax·ai Bot" ? <div className="meta-history-log-icon meta-history-log-icon-image" style={{ backgroundImage: "url('https://static.stax.ai/images/icons/bot.png')" }}></div> : <div
                    className={"meta-history-log-icon palette-color-" + String(offsetIdx%8)}
                    title={userName}
                >{userName.substring(0,1)}</div>}
                {log.event}
                <div className="meta-history-log-user" title={userName}>{userName}</div>
                <div className="meta-history-log-time">{formatDate(log.stamp)}</div>
            </div>
        </React.Fragment>);
    }

    renderSplitDoc = (d, idx, doc,) => {
        return (
            <React.Fragment key={idx}>
                {idx > 0? <div className='meta-automate-pipeline-pipe' />: null}
                <div className="meta-document-splits">
                    <div
                        style={{width: '100%',display: 'flex',alignItems: 'center'}}
                    >
                        <div 
                            className="meta-document-splits-name"
                            style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                            title={d.name}
                            onClick={() => window.open(api.publicUrl + "/document/" + d._id)}>
                                    {d.name}      
                        </div>
                        <img
                            src="https://static.stax.ai/images/icons/delete.png"
                            alt="(-)"
                            title="Delete Statement"
                            style={{ cursor: 'pointer', marginLeft: 'auto', marginRight: 10, width: 14, height: 14 }}
                            onClick={() => this.props.modal("confirm", {
                                message: <>You are about to delete <strong> {d.name} </strong>. 
                                All other team users will lose access to this document as well.</>,
                                action: async () => {
                                    this.props.updateDocument(doc._id, { 
                                        splits: doc.splits.filter(s => s._id !== d._id)
                                    }, false);
                                    await api.post('/document/remove', {docId: d._id});
                            }})}
                        />
                    </div>
                    <div className="meta-document-splits-stack"
                            style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                            title={this.getStackPath(d.stack)} >
                            {this.getStackPath(d.stack)}
                    </div>
                </div>               
            </React.Fragment>
        )
    }

    containsSpecialChars(str) {
        const specialChars = /[`@#$%^&*()_+\-=[\]{};:\\|<>/?~]/;
        return specialChars.test(str);
    }

    retryFailed = async (data, pipeline, step, error="") => {
        this.setState({ retryable: false });
        toast.success("The document will retry the failed job and resume the automation pipeline.");
        const doc = this.props.document;

        // Unflag document
        await this.props.updateDocument(doc._id, {
            flagged: false
        });

        // Queue pipeline from provided step ID
        const res = await api.post('/job/queue', {
            stack: data.stack,
            stepId: pipeline[step]?._id,
            moduleId: pipeline[step]?.module,
            documentId: doc._id
        });

        if (this.props.user?.email?.endsWith("@stax.ai")) {
            // Potentially a Stax.ai admin team member - record split document action
            await api.post('/internal/record', {
                user: this.props.user.email,
                team: this.props.team.name,
                code: "Retry",
                points: 1,
                document: doc._id,
                error
            });
        }

        if (res && res.job) {
            // Locally add job to document jobs
            const jobs = [ ...doc.jobs, res.job ];
            this.props.updateDocument(doc._id, { jobs }, false);
        }
        
        this.setState({ retryable: true });
    }

    retrySaving = async (doc, syncIdx) => {       

        const sync = doc.sync?.[syncIdx];
        if(!sync)
            return toast.error("Could not find sync data for this document. Rerun the pipeline on it.")

        const pathValue = doc.metadata.find(({key}) => key === sync.key)?.value;

        if(!pathValue){
            toast.error(`Please provide a value for ${sync.key}`);
            return;
        }

        toast.success("Retry saving document shortly.");
        delete sync.error; 
        sync.path = pathValue.replace(/'/g, "\\u0027").replace(/’/g, "\\u2019"); 
        doc.sync[syncIdx] = sync;
        
        // Unflag document & clear the error to re-sync with the sync app
        await this.props.updateDocument(doc._id, {
            flagged: false, sync: doc.sync
        });
    }    

    resumeNext = async (data, pipeline, next) => {
        this.setState({ resumable: false });
        toast.success("The document will resume the automation pipeline for this stack.");
        const doc = this.props.document;

        // Unflag document
        await this.props.updateDocument(doc._id, {
            flagged: false
        });

        // Queue pipeline from provided step ID
        const res = await api.post('/job/queue', {
            stack: data.stack,
            stepId: pipeline[next]._id,
            moduleId: pipeline[next].module,
            documentId: doc._id
        });

        if (res && res.job) {
            // Locally add job to document jobs
            const jobs = [ ...doc.jobs, res.job ];
            this.props.updateDocument(doc._id, { jobs }, false);
        }
        
        this.setState({ resumable: true });
    }

    renderAutomateJob = (job, idx, isLast) => {

        // This is a triggered module
        if (job.jobType && job.jobType.includes("module-")) {
            const moduleId = job.jobType.split("-")[1];
            const module = this.props.modules.find(m => m._id === moduleId);
            const data = job.jobData ? job.jobData : {};
            const stack = this.props.stacks.find(s => s._id === (data && data.stack ? data.stack : null));

            if (!module)
                return (<div key={idx} />);

            let timedOut;
            try {
                timedOut = (new Date() - new Date(Date.parse(job.queueTime))) / 60000 > 120;
            } catch (error) {
                timedOut = true; // Don't know what happened...
            }

            // Check if there is a step after this in the stack's automate pipeline...
            const pipeline = stack?.pipeline ? stack.pipeline : [];
            let step, next;
            if (isLast) {
                step = pipeline.findIndex(s => s._id === data.stepId);
                if (step >= 0 && step < pipeline.length-1)
                    next = step + 1;
            }

            return (<div key={idx} style={{ position: 'relative' }}>
                <div className="meta-automate-pipeline-pipe" />
                <div
                    className="meta-history-log"
                    style={{
                        backgroundImage: "url('" + module.icon + "')"
                    }}
                    title={module.name}
                >
                    {module.name}
                    <div className="meta-history-log-user" title={stack?.path??(data.manual?'Manually ran':'Deleted stack')}>{stack?.path??(data.manual?'Manually ran':'Deleted stack')}</div>
                    <div className="meta-history-log-time">{formatDate(job.endTime)}</div>
                </div>
                
                {/* In progress... */}
                {!job.error && job.working && !job.success && !timedOut && <div
                    className="meta-automate-pipeline-step-processing"
                    title={"Started at " + formatDate(job.queueTime)}
                    // style={{
                    //     animation: "spin 1.5s linear infinite"
                    // }}
                >
                    <Loading
                        type="spin"
                        color="var(--color-primary)"
                        height={24}
                        width={24}
                    />
                </div>}

                {/* Errored or timed out */}
                {!job.success && (job.error || timedOut) && <div
                    className="meta-automate-log-container"
                    style={isLast ? {
                        borderLeft: '2px solid transparent'
                    } : {}}
                >
                    <div className="meta-automate-log-error"
                        onContextMenu={(e) => this.openCopyErrorMenu(e, job.error ?? "Oops, something went wrong! Your job is taking longer than it should, so we stopped it.")}
                    >
                        {
                            this.props.user.email.includes("@stax.ai") || !this.containsSpecialChars(job.error) ? 
                            job.error? job.error: <>Oops, something went wrong!<br />Your job is taking longer than it should, so we stopped it.</>
                            :
                            job.error? 
                            <>
                                This step failed with an error!<br />
                                If you are not sure how to address the error below, start a live chat with us.
                                <br/><br/>
                                {job.error}
                                
                            </>
                            : 
                            <>Oops, something went wrong!<br />Your job is taking longer than it should, so we stopped it.</>
                        }
                    </div>
                    {/*Open context menu to copy value*/}
                    <ContextMenu
                        load={(open,close) => {
                            this.openCopyErrorMenu = open;
                            this.closeCopyErrorMenu = close;
                        }}
                        options={[
                            {
                                label: 'Copy Error',
                                action: (err) => {
                                    copyText(err);
                                    toast.info("Copied to clipboard");
                                }
                            }
                        ]}
                    />
                    {/* If there is a possible next step, allow user to retry last, or resume */}
                    <div className="meta-automate-actions">
                        {this.state.retryable && stack && isLast && !job.error && <div
                            className="meta-automate-retry"
                            title="Unflag document and retry the previously failed step."
                            onClick={() => this.retryFailed(data, pipeline, step)}
                        >Retry failed</div>}

                        {this.state.resumable && stack && next && isLast && !job.error && <div
                            className="meta-automate-resume"
                            title="Unflag document and continue processing the next step."
                            onClick={() => this.resumeNext(data, pipeline, next)}
                        >Resume next</div>}
                    </div>
                </div>}

                {/* If there is a possible next step, allow user to retry last, or resume */}
                <div className="meta-automate-actions">

                    {job.error && this.state.retryable && stack && isLast && <div
                        className="meta-automate-retry"
                        title="Unflag document and retry the previously failed step."
                        onClick={() => this.retryFailed(data, pipeline, step, job.error)}
                    >Retry failed</div>}

                    {job.error && this.state.resumable && stack && next && isLast && <div
                        className="meta-automate-resume"
                        title="Unflag document and continue processing the next step."
                        onClick={() => this.resumeNext(data, pipeline, next)}
                    >Resume next</div>}
                </div>
            </div>);
        }

        return <div key={idx} />;
    }

    editFillableField = async (doc, idx, diff, cloud=true) => {
        if (!doc.fillable || !doc.fillable.fields)
            return;

        let fields = [ ...doc.fillable.fields ];
        fields[idx] = {
            ...fields[idx],
            ...diff
        };

        await this.props.updateDocument(doc._id, {
            fillable: {
                ...doc.fillable,
                fields
            }
        }, cloud);
    }

    editFillableFieldGroup = async (doc, key, diff) => {
        if (!doc.fillable || !doc.fillable.fields)
            return;

        let fields = [ ...doc.fillable.fields ];
        for (let i=0; i<fields.length; i++) {
            if (fields[i].key === key)
                fields[i] = {
                    ...fields[i],
                    ...diff
                };
        }

        await this.props.updateDocument(doc._id, {
            fillable: {
                ...doc.fillable,
                fields
            }
        });
    }

    setInputBounds = (doc, field) => {
        this.setState({ editInputBounds: field.idx });

        if (!this.labelMessageShown) {
            toast.info(<>Draw a rectangle on the page where you'd like to fill the value for: <strong>{field && field.key ? field.key.toUpperCase() : "Unnamed Input"}</strong></>);
            this.labelMessageShown = true;
        }

        this.props.setFunction("draw-input-bounds", {
            callback: async (page,x,y,width,height) => {
                await this.editFillableField(doc, field.idx, {
                    ...field,
                    page,
                    x,
                    y,
                    width,
                    height,
                    idx: undefined
                });
                this.setState({
                    editInputBounds: null
                });
                this.props.setFunction();
            }
        });
    }

    retagField = (doc, field) => {
        this.setState({
            retagMetaField: field.idx,
            editMeta: doc._id,
            editParagraph: null
        });

        if (!this.retagMessageShown) {
            toast.info(<>Select text on the document to tag for field: <strong>{field && field.key ? field.key.toUpperCase() : "Unnamed Field"}</strong></>);
            this.retagMessageShown = true;
        }

        this.props.setFunction("retag-field", {
            callback: async (words) => {
                if (!words || words.length === 0) return;
                let metadata = [ ...this.state.metadata ];
                metadata[field.idx].pages = [ words[0].page ];
                metadata[field.idx].value = words.map(w => w.text).join(' ');
                metadata[field.idx].tags = words.map(w => w.wordIdx);
                metadata[field.idx].confidence = undefined; // Remove confidence
                this.setState({ metadata, metaChanged: true, retagMetaField: null });
                this.props.setFunction();
            }
        });
    }

    renderDocStatement = (doc, statement) => {
        if (!statement) return (<></>);
        if (statement.forDocument !== doc._id) return (<></>);

        const attachStatement = async () => {
            this.props.loading(1005);
            const res = await api.post('/tpa/statement/attach', { docId: doc._id });
            if (!res?.statement) {
                toast.error(res?.message ?? "Failed to attach statement to account. Please verify that the plan ID, account number, and period end date are correct.");
                return this.props.loaded()
            };
            toast.success("Document successfully linked to account.");
            this.props.setStatement({
                ...res.statement,
                forDocument: doc._id
            });
            this.props.loaded();
        };

        const confirmDetach = () => {
            if ((statement?.confirmed && this.props.user.email.endsWith("@stax.ai")) || (statement?.clientConfrimed && !this.props.user.email.endsWith("@stax.ai"))) {
                this.props.modal("confirm", {
                    message: <>You are about to detach an accepted statement. </>,
                    action: async () => {
                        detachStatement();
                    }
                })
            }
            else {
                detachStatement();
            }
        }

        const detachStatement = async () => {
            this.props.loading(1005);
            const res = await api.post('/tpa/statement/remove', { docId: doc._id });
            if (!res?.statement) {
                toast.error(res?.message ?? "Failed to detach statement from account. Please verify that this is a statement.");
                return this.props.loaded()
            };
            toast.success("Document successfully detached from account.");
            this.props.setStatement({forDocument: doc._id});
            this.props.loaded();
        };

        const userPlanYear = this.props.user.state?.planYear;

        if (statement._id){
            const statementPlanYear = new Date(statement.periodEnd).getFullYear(); 
                
            return (<>
                <h1>Statement</h1>
                {doc._id === statement.document ? <>
                    {/* This is the document associated with the statement */}
                    <p>
                        {
                            statementPlanYear !== userPlanYear && <>
                                This is a <strong>{statementPlanYear}</strong> statement, but your Investment Dashboard preference is set to the <strong>{userPlanYear}</strong> plan year.
                                <br /><br />
                            </>
                        }

                        <strong>{statement.periodEnd ? (new Date(statement.periodEnd)?.toLocaleString('en-US', {
                            day: "numeric",
                            month: "long",
                            year: "numeric"
                        }) ?? "") : ""}</strong> is registered to this document as the statement for <strong>{statement.planId}</strong> / <strong>{statement.accountNumber}</strong>.

                        <br /><br />
                        If this information is incorrect, you can change it by editing the <i>document's fields</i> below.
                    </p>
                    <div
                        className="meta-action"
                        title="Detach statement"
                        style={{
                            backgroundImage: "url('https://static.stax.ai/images/icons/chart.png",
                            marginTop: 10
                        }}
                        onClick = {confirmDetach}
                    >Detach from {statementPlanYear} plan year</div>
                </> : <>
                    {/* This document might be a duplicate, but also may not...  */}
                    <p>
                        {
                            statementPlanYear !== userPlanYear && <>
                                This is a <strong>{statementPlanYear}</strong> statement, but your Investment Dashboard preference is set to the <strong>{userPlanYear}</strong> plan year.
                                <br /><br />
                            </>
                        }

                        <strong>{statement.periodEnd ? (new Date(statement.periodEnd)?.toLocaleString('en-US', {
                            day: "numeric",
                            month: "long",
                            year: "numeric"
                        }) ?? "") : ""}</strong> is registered to this document as the statement for <strong>{statement.planId}</strong> / <strong>{statement.accountNumber}</strong>.

                        <div
                            className="meta-duplicates-document"
                            style={{ marginTop: 20 }}
                        >
                            <div
                                title={"Approved Statement"}
                                className="meta-duplicates-document-name"
                                onClick={() => window.open(api.publicUrl + "/stack/" + (doc.stack ? doc.stack : "unclassified") + "/document/" + statement.document, "_blank")}
                            >View Approved Statement</div>
                            <div className="meta-duplicates-document-stack">{this.getStackPath(doc.stack)}</div>
                        </div>

                        <div
                            className="meta-action"
                            title="Attach document and overwrite"
                            style={{
                                backgroundImage: "url('https://static.stax.ai/images/icons/chart.png",
                                marginTop: 10
                            }}
                            onClick={attachStatement}
                        >Link document and overwrite</div>
                    </p>
                </>}
            </>);
        }
        // Could this document be a statement?
        const planId = doc.metadata.find(f => f.key === "plan id")?.value;
        // const planYear = doc.metadata.find(f => f.key === "plan year")?.value;
        const accountNumber = doc.metadata.find(f => f.key === "account")?.value;
        let periodEnd = doc.metadata.find(f => f.key === "period end")?.value;
        if (periodEnd) periodEnd = new Date(periodEnd);
        if (!planId || !accountNumber || !periodEnd) return (<></>);

        return (<>
            <h1>Statement</h1>
            <p>

                {
                    periodEnd.getFullYear() !== userPlanYear && <>
                        This is a <strong>{periodEnd.getFullYear()}</strong> statement, but your Investment Dashboard preference is set to the <strong>{userPlanYear}</strong> plan year.
                        <br /><br />
                    </>
                }

                <strong>{periodEnd ? (new Date(periodEnd)?.toLocaleString('en-US', {
                    day: "numeric",
                    month: "long",
                    year: "numeric"
                }) ?? "") : ""}</strong> is registered to this document as the statement for <strong>{planId}</strong> / <strong>{accountNumber}</strong>.

                <div
                    className="meta-action"
                    title="Attach document and overwrite"
                    style={{
                        backgroundImage: "url('https://static.stax.ai/images/icons/chart.png",
                        marginTop: 10
                    }}
                    onClick={attachStatement}
                >Link document to account</div>
            </p>
        </>);
    }

    renderDocFlags = (doc) => {
        if (!doc?.flags || doc.flags.length === 0) return (<></>);
        let flags = [...doc.flags]; 
        if(!this.state.showAllFlags){ 
            flags = flags.slice(0,3); 
        } 

        return (<>
            <h1>Flags</h1>
            {flags.map((flag,idx) => <div
                className="meta-automate-log-container"
                style={{
                    borderLeft: '2px solid transparent',
                    marginLeft: 0,
                    paddingBottom: 0
                }}
            >
                <div
                    className="meta-automate-log-error"
                    style={{
                        marginLeft: 0,
                        width: 'calc(100% - 80px)',
                        marginBottom: 10,
                        backgroundImage: "url('https://static.stax.ai/images/icons/" + (flag.level === 'error' ? 'error' : flag.level === 'warn' ? 'alert' : 'info') + ".png')",
                        ...(flag.level === 'info' ? {
                            backgroundColor: 'rgba(120,120,120,0.12)',
                            borderColor: 'rgba(120,120,120,0.5)'
                        } : {})
                    }}
                >
                    <h1>{flag.title}</h1>
                    <p>{renderHTML(flag.description)}</p>
                    {flag.pages?.length > 0 ? <div
                        className="meta-automate-log-jump-page"
                        title="Jump to page"
                        onClick={() => {
                            const page = flag.pages[0];
                            setTimeout(() => { // auto scroll to the page 
                                const pageDiv = document.getElementById('page-' + String(page));
                                if (pageDiv) pageDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 100);
                        }}
                    >
                        {flag.pages.length > 1 ? `Scroll to pages: ${flag.pages[0]+1}-${flag.pages[flag.pages.length-1]+1}` : `Scroll to page: ${flag.pages[0]+1}`}
                    </div> : ""}

                    {/* Clear flag */}
                    <div
                        className="meta-automate-log-clear"
                        title="Clear flag"
                        onClick={async () => {
                            await this.props.updateDocument(doc._id, {
                                flags: doc.flags.filter(f => String(f._id) !== String(flag._id))
                            }, false);
                            
                            // The actual (trackable) cloud update
                            await api.post('/document/unflag', {
                                docId: doc._id,
                                flagId: flag._id
                            });
                        }}
                    />
                </div>
            </div>)}
            {
                doc.flags.length > 3 && 
                <div
                    className='meta-showmore'
                    onClick={() => this.setState({showAllFlags: !this.state.showAllFlags})}
                >
                    {this.state.showAllFlags? "Show fewer flags...": "Show more flags..."}
                </div>
            }
        </>);
    }

    editMeta = (doc) => {
        // We started editing fields: get our real stack
        this.fetchStackKeys(); // Synchronously

        let metadata = [ ...doc.metadata ];
        if (metadata.length === 0)
            metadata.push({ key: "", value: "" });
        this.setState({
            editMeta: doc._id,
            editParagraph: null,
            metadata,
            sortMetaAscending: null
        });

        // Add an event listener to the page for cntrl + s
        const saveListener =  document.addEventListener("keydown", (e) => {
            if(!e.ctrlKey || e.key !== "s") return; 
            e.stopPropagation(); 
            e.preventDefault(); 
            this.saveMetadata();
            document.removeEventListener('keydown', saveListener);
        });
        document.addEventListener("keydown", saveListener);
    };

    sortMeta = (doc) => {
        this.setState({"sortMetaAscending": !this.state.sortMetaAscending});
    }

    renderDocFields = (user, team, doc, stack) => {
        const keys = this.state.keys ?? [];
        const defaultKeys = this.props.team?.scopes?.includes('tpa') ? [
            'account',
            'provider',
            'payee',
            'plan id',
            'plan name',
            'local path',
            'directory',
            'plan status',
            'plan year end',
            'period start',
            'period end',
            'beginning balance',
            'ending balance',
            'gain/loss',
            'tx: deposits',
            'tx: withdrawals',
            'tx: income',
            'tx: fees',
            'tx: purchases',
            'tx: sales',
            'tx: other',
            'tx: transfers'
        ] : [];
        const taOK = reconcileTA(doc);
        return (<>
        <div
            style={{
                display: 'flex', 
                alignItems: 'center'
            }}
        >
            <h1>Fields{this.state.editMeta === doc._id ?
                    <>
                        <img
                            className="metadata-to-be-edited-icon"
                            src="https://static.stax.ai/images/icons/confirm.png"
                            alt="Edit"
                            title="Click to save edits"
                            onClick={this.saveMetadata}
                        />
                        <span
                            className='a'
                            style={{ marginLeft: '6px', fontSize: '14px' }}
                            onClick={this.saveMetadata}
                        >Save
                        </span>
                    </>
                    :
                    <>
                        <img
                            className="metadata-edit-icon"
                            src="https://static.stax.ai/images/icons/edit.png"
                            alt="Edit"
                            title="Click to edit metadata"
                            onClick={() => this.editMeta(doc)}
                        />
                        <img
                            className="metadata-sort-icon"
                            src= { this.state.sortMetaAscending ? "https://static.stax.ai/images/icons/section-collapse.png" : "https://static.stax.ai/images/icons/section-expand.png"}
                            alt="Sort"
                            title="Click to sort metadata"
                            onClick={() => this.sortMeta(doc)}
                        />
                    </>
                }                                         
            </h1>
        </div>


            {this.state.editMeta === doc._id ? this.state.metadata.map((field,idx) => <React.Fragment key={idx+"-edit"}>
                <div className="metadata-field-editable">
                    {/* {field.tags && field.tags.length > 0 ?  */}
                    <div className="metadata-edit-key-select">
                        <CreatableSelect
                            className="metadata-select-container"
                            classNamePrefix="metadata-select"
                            value={{
                                value: field.key,
                                label: field.key ? field.key.toUpperCase() : ""
                            }}
                            options={[...defaultKeys, ...keys].length > 0 ? [...new Set([...defaultKeys, ...keys])].map(key => ({
                                value: key,
                                label: key?.toUpperCase()
                            })) : []}
                            isSearchable={true}
                            placeholder="Select Key"
                            onChange={async (option) => {
                                let metadata = [ ...this.state.metadata ];
                                metadata[idx].key = option.value.toLowerCase();
                                this.setState({ metadata, metaChanged: true });

                                // Add key to stack?
                                if (!keys?.includes(option.value.toLowerCase()) && stack?._id?.length === 24)
                                    this.props.updateStack(stack._id, {
                                        keys: [
                                            ...(keys ?? []),
                                            option.value.toLowerCase()
                                        ]
                                    });
                            }}
                        />
                    </div>
                    {/* : <input
                        id={"key-"+idx}
                        name={"key-"+idx}
                        type="text"
                        className="metadata-edit-key"
                        value={field.key}
                        onChange={e => {
                            // Modifying state directly, but doesn't seem to be an issue
                            // let metadata = JSON.parse(JSON.stringify(this.state.metadata));
                            let metadata = [...this.state.metadata]
                            metadata[idx].key = e.target.value.toLowerCase();
                            this.setState({ metadata, metaChanged: true });
                        }}
                    /> */}
                    : 
                    {this.state.retagMetaField === idx ? <div
                        className="metadata-edit-value-retag-message"
                    >Select text to tag...</div> : <input
                        id={"value-"+idx}
                        name={"value-"+idx}
                        type="text"
                        // className={"metadata-edit-value" + (field.tags && field.tags.length ? " metadata-edit-value-tagged" : "")}
                        className={"metadata-edit-value metadata-edit-value-tagged"}
                        style={this.state.editParagraph === idx ? { color: 'rgb(127,127,127)' } : {}}
                        value={this.state.editParagraph === idx ? "Edit below..." : field.expression ?? field.value}
                        onChange={e => {
                            if (this.state.editParagraph === idx) return;

                            // Modifying state directly, but doesn't seem to be an issue
                            // let metadata = JSON.parse(JSON.stringify(this.state.metadata));
                            let metadata = [...this.state.metadata]
                            
                            // If it has a confidence, set it to 100% 
                            if (metadata[idx].confidence) {
                                metadata[idx].confidence = null;
                                delete metadata[idx].confidence;
                            }

                            // Expression?
                            if (e.target.value[0] === '=') {
                                metadata[idx].expression = e.target.value;
                                // metadata[idx].value = evaluateField(metadata, metadata[idx].key);
                            } else {
                                metadata[idx].expression = null;
                                metadata[idx].value = e.target.value;
                            }
                            this.setState({ metadata, metaChanged: true });
                        }}
                        onKeyDown={(e) => inputKey(e, {
                            "Enter": this.saveMetadata
                        })}
                    />}
                    {field.tags && field.tags.length ? <img
                        className="metadata-edit-redo"
                        src="https://static.stax.ai/images/icons/redo.png"
                        alt="(-)"
                        title="Re-tag metadata on page"
                        onClick={() => this.retagField(doc, { ...field, idx })}
                    /> : <img
                        className="metadata-edit-paragraph"
                        src="https://static.stax.ai/images/icons/paragraph.png"
                        alt=""
                        title="Edit paragraph text"
                        onClick={() => this.setState({ editParagraph: this.state.editParagraph === idx ? null : idx })}
                    />}
                    <img
                        className="metadata-edit-delete"
                        src="https://static.stax.ai/images/icons/remove.png"
                        alt="(-)"
                        title="Delete metadata field"
                        onClick={() => {
                            // Modifying state directly, but doesn't seem to be an issue
                            // let metadata = JSON.parse(JSON.stringify(this.state.metadata));
                            let metadata = [...this.state.metadata]
                            metadata.splice(idx,1);
                            this.setState({ metadata, metaChanged: true, retagMetaField: null });
                            this.props.setFunction();
                        }}
                    />
                </div>
                {/* Edit paragraph? */}
                {this.state.editParagraph === idx ? <div className="metadata-edit-paragraph-container">
                    <textarea
                        placeholder="Type notes, summary, or any text..."
                        onChange={e => {
                            // Modifying state directly, but doesn't seem to be an issue
                            // let metadata = JSON.parse(JSON.stringify(this.state.metadata));
                            let metadata = [...this.state.metadata];
                            if (e.target.value[0] === '=') {
                                metadata[idx].expression = e.target.value;
                                // metadata[idx].value = evaluateField(metadata, metadata[idx].key);
                            } else {
                                metadata[idx].expression = null;
                                metadata[idx].value = e.target.value;
                            }
                            this.setState({ metadata, metaChanged: true });
                        }}
                        value={field.expression ?? field.value}
                        onKeyDown={(e) => inputKey(e, {
                            "Enter": this.saveMetadata
                        })}
                    >
                    </textarea>
                </div> : ""}
            </React.Fragment>) : doc.metadata ? <>
                {(  
                    this.state.sortMetaAscending === true ? doc.metadata.sort((a,b) => a.key.localeCompare(b.key)) :
                    this.state.sortMetaAscending === false ? doc.metadata.sort((a,b) => b.key.localeCompare(a.key)) :
                    doc.metadata
                ).slice(0, this.state.showAllMeta ? undefined : 5).map((field,idx) => <div
                    className="metadata-field"
                    key={idx}
                    onMouseEnter={() => this.props.hover({ tag: field })}
                    onMouseLeave={() => this.props.hover(null)}
                >
                    <div
                        className="meta-details-label"
                        title={field.key?.toUpperCase()}
                    >{field.confidence ? (
                        field.confidence < 0.6 ? <div className="meta-details-field-confidence-low" /> :
                        field.confidence < 0.9 ? <div className="meta-details-field-confidence-medium" /> : ""
                    ) : ""} <span className='meta-details-field-key'>{field.key}</span></div>

                    <div
                        className="meta-details-value"
                        title={
                            (field.confidence ? ("[" + (100*field.confidence).toFixed(1) + "%] ") : "") + field.value + (field.expression ? " " + field.expression : "") + 
                            (field.ref ? `\nRef: ${field.ref}`: "") + 
                            (field?.pages?.length ? field?.pages?.length === 1 ? "\n(page " + String(field?.pages[0]+1) + ")": "\n(pages " + String(field?.pages[0]+1) + " - " + String(field?.pages[field?.pages?.length-1]+1) + ")":"") 

                        }
                        onDoubleClick={() => this.editMeta(doc)}

                        onContextMenu={(e) => {
                            this.setState({selectedMetaFieldIdx: idx})
                            this.openCopyMetaMenu(e, {"key": field.key, "value": field.value, "idx": idx})}
                        }
                    >
                        {field.tags?.length > 0 ? <img
                            className="meta-details-tag"
                            src="https://static.stax.ai/images/icons/tag.png"
                            alt="[TAG]"
                        /> : ""}
                        {field.expression || field.value?.[0] === '=' ? <>
                            {/* Evaluate expression */}
                            <span className="meta-eval">=</span>
                            {evaluateField(doc.metadata, field.key)}
                        </> : field.value}
                    </div>
                </div>)
                }
                
                <ContextMenu
                    load={(open,close) => {
                        this.openCopyMetaMenu = open;
                        this.closeCopyMetaMenu = close;
                    }}
                    options={this.getContextMenuOptions(doc)}
                />
                
                <ContextMenu
                    load={(open,close) => {
                        this.openRefMetaMenu = open;
                        this.closeRefMetaMenu = close;
                    }}
                    options={[
                        {
                            label: 'Since last statement',
                            action: ({meta}) => {
                                // Get the document metadata... 
                                const metadata = this.props.document.metadata;
                                // Add the reference to the metadata... 
                                metadata[meta.idx].ref = "Since last statement";

                                this.props.updateDocument(this.props.document._id, {metadata})
                            }
                        },
                        {
                            label: 'Current month',
                            action: ({meta}) => {
                                // Get the document metadata... 
                                const metadata = this.props.document.metadata;
                                // Add the reference to the metadata... 
                                metadata[meta.idx].ref = "Current Month";

                                this.props.updateDocument(this.props.document._id, {metadata})
                            }
                        },
                        {
                            label: 'Quarter to date',
                            action: ({meta}) => {
                                // Get the document metadata... 
                                const metadata = this.props.document.metadata;
                                // Add the reference to the metadata... 
                                metadata[meta.idx].ref = "Quarter to Date";

                                this.props.updateDocument(this.props.document._id, {metadata})
                            }
                        },
                        {
                            label: 'Quarterly',
                            action: ({meta}) => {
                                // Get the document metadata... 
                                const metadata = this.props.document.metadata;
                                // Add the reference to the metadata... 
                                metadata[meta.idx].ref = "Quarterly";

                                this.props.updateDocument(this.props.document._id, {metadata})
                            }
                        },
                        {
                            label: 'Year to date',
                            action: ({meta}) => {
                                // Get the document metadata... 
                                const metadata = this.props.document.metadata;
                                // Add the reference to the metadata... 
                                metadata[meta.idx].ref = "Year to Date";
                                this.props.updateDocument(this.props.document._id, {metadata})
                            }
                        }
                    ]}
                />
                {doc.metadata.length > 5 && <div
                    className="meta-showmore"
                    onClick={() => this.setState({ showAllMeta: !this.state.showAllMeta })}
                >Show {this.state.showAllMeta ? "fewer": "more"} fields...</div>}
            </> : ""}

            {/* Add a metadata key-value pair (field) */}
            <div
                className="meta-action"
                title="Add a new key-value pair"
                style={{
                    backgroundImage: "url('https://static.stax.ai/images/icons/action-create.png",
                    marginTop: 3,
                    clear: 'both'
                }}
                onClick={() => {
                    this.fetchStackKeys(); // Synchronously

                    this.setState({
                        editMeta: doc._id,
                        editParagraph: null,
                        metadata: [
                            ...(this.state.editMeta === doc._id ? this.state.metadata : doc.metadata),
                            {
                                key: "",
                                value: ""
                            }
                        ]
                    });
                }}
            >New field</div>

            {/* If failed to reconcile (taOK < 0) and gain/loss isn't set, allow users to auto-calculate based on existing information. */}
            {taOK !== true && !doc.metadata.find(f => f.key === 'gain/loss') ? <div
                className="meta-action"
                title="Auto-calculate gain/(loss)"
                style={{
                    backgroundImage: "url('https://static.stax.ai/images/icons/calculator.png",
                    marginTop: 0
                }}
                onClick={() => {
                    // Gain/loss = ending balance - beginning balance - deposits - withdrawals - income - fees - other - transfers
                    const beginning = get(doc, 'beginning balance');
                    const ending = get(doc, 'ending balance');
                    const deposits = get(doc, 'tx: deposits');
                    const withdrawals = get(doc, 'tx: withdrawals');
                    const income = get(doc, 'tx: income');
                    const fees = get(doc, 'tx: fees');
                    const transfers = get(doc, 'tx: transfers');
                    const other = get(doc, 'tx: other');
                    const reinvestments = get(doc, 'tx: reinvestments'); 
                    const loanPayments = get(doc, 'tx: loan payments'); 
                    const receivables = get(doc, 'tx: receivables');
                    const gain = ending - beginning - deposits - withdrawals - income - fees - other - transfers - reinvestments - loanPayments - receivables;

                    let gainString = Math.abs(gain).toLocaleString();
                    if (gain < 0)
                        gainString = "(" + gainString + ")";

                    // Add gain/(loss) to metadata
                    let metadata = this.state.editMeta === doc._id ? [ ...this.state.metadata ] : [ ...doc.metadata ];
                    metadata.push({
                        key: 'gain/loss',
                        value: gainString
                    });
                    this.setState({ metadata, metaChanged: true, editMeta: doc._id });
                    if (this.props.user?.email?.endsWith("@stax.ai")) {
                        // Potentially a Stax.ai admin team member - record action
                        api.post('/internal/record', {
                            user: this.props.user.email,
                            team: this.props.team.name,
                            code: "Auto-Calc",
                            points: 5,
                            document: this.props.document._id,
                        });
                    }
                }}
            >Auto-calculate gain/(loss)</div> : ""}

            {/* Check for TA reconciliation */}
            {taOK === 0 ? "" : <div
                className="meta-reconcile"
                style={{
                    backgroundImage: `url('https://static.stax.ai/images/icons/${taOK === true ? "check" : "error"}.png')`
                }}
            >{taOK === true ? 
                "Gain/(loss) reconciliation OK" : 
                <>
                Failed to reconcile gain/(loss)
                <div style={{color: 'var(--color-text-light)'}}>
                    {typeof taOK === 'number' ? `Difference: ${taOK.toFixed(2)}` : taOK}
                </div>
                </>
            }
                
            </div>}
        </>);
    }

    renderDocHistory = (user, team, doc, stack) => <>
        {doc.time && doc.time.length > 0 && <>
            {/* Document update history */}
            <h1 title="Here is a full trail of changes made to the document since it was received in Stax·ai.">History</h1>

            {this.state.showFullHistory || doc.time.length < 6 ? doc.time.map((log,idx) => this.renderLogEvent(log,idx,user,team)) : <>
                {doc.time.slice(0,3).map((log,idx) => this.renderLogEvent(log,idx,user,team))}

                <div className="meta-history-log-skip" onClick={() => this.setState({ showFullHistory: true })} />

                {this.renderLogEvent(doc.time[doc.time.length-1],-1,user,team)}
            </>}

            {doc.time.length > 3 && <div
                className="meta-showmore"
                onClick={() => this.setState({ showFullHistory: !this.state.showFullHistory })}
            >{this.state.showFullHistory ? "Collapse history...": "Show full history..."}</div>}
        </>}
    </>

    

    renderDocAutomate = (user, team, doc, stack) => <>
        {doc.jobs && doc.jobs.length > 0 && <>
            {/* Automation modules */}
            <h1 title="These modules have performed some operations on this document.">Automate</h1>

            <div
                className="meta-automate-pipeline-step"
                style={{ backgroundImage: "url('https://static.stax.ai/images/icons/" + (this.props.user && this.props.user.enableDarkMode ? "dark/" : "") + "folder.png')" }}
            >Document received in stack</div>

            {this.state.showFullAutomate || doc.jobs.length < 4 ? doc.jobs.map((job,idx) => this.renderAutomateJob(job, idx, idx === doc.jobs.length-1)) : <>
                {doc.jobs.slice(0,2).map((job,idx) => this.renderAutomateJob(job, idx, idx === doc.jobs.length-1))}

                <div className="meta-history-log-skip" onClick={() => this.setState({ showFullAutomate: true })} />

                {this.renderAutomateJob(doc.jobs[doc.jobs.length-1],-1, true)}
            </>}

            {doc.jobs.length > 3 && <div
                className="meta-showmore"
                onClick={() => this.setState({ showFullAutomate: !this.state.showFullAutomate })}
            >{this.state.showFullAutomate ? "Collapse jobs...": "Show all jobs..."}</div>}
        </>}
    </>

    renderSplits = (user, team, doc, stack) => <>
        {/* Listing Splits from this doc */}
        {doc.splits && doc.splits.length > 0 && <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h1>Splits</h1>
            </div>
            {this.state.showFullSplits || doc.splits.length < 4 ? doc.splits.map((d, docIdx) => this.renderSplitDoc(d, docIdx, doc)) : <>
                {doc.splits.slice(0, 3).map((d, docIdx) => this.renderSplitDoc(d, docIdx, doc))}
            </>}
            {this.state.showFullSplits && <div
                className="meta-action"
                style={{
                    backgroundImage: "url('https://static.stax.ai/images/icons/delete.png",
                    marginTop: 12,
                    marginLeft: 14,
                    padding: '0px 30px',
                    lineHeight: '42px'  
                }}
                onClick={() => this.props.modal("confirm", {
                    message: <>You are about to delete <strong> {doc.splits.length} documents </strong>.
                        All other team users will lose access to these documents as well.</>,
                    action: async () => {
                        this.props.updateDocument(doc._id, {
                            splits: []
                        }, false);
                        await api.post('/document/remove', { docIds: doc.splits.map(d => d._id) });
                    }
                })}
            >
                Delete All Splits</div>}
            {doc.splits.length > 3 && <div
                className="meta-showmore"
                onClick={() => this.setState({ showFullSplits: !this.state.showFullSplits })}
            >{this.state.showFullSplits ? "Collapse all splits..." : "Show all splits..."}</div>}
        </>
        }
    </>

    renderDocDuplicates = (user, team, doc, stack) => <>
        {/* Duplicates? */}
        {doc.original ? <>
            <h1>Original</h1>
            <p>This document is a <strong>duplicate</strong>. See <a href={api.publicUrl + "/stack/" + (stack && stack._id ? stack._id : "unclassified") + "/document/" + doc.original}>original.</a></p>
        </> : doc.duplicates && doc.duplicates.length > 0 ? <>
            <h1>Duplicates</h1>
            {doc.duplicates.map((doc,docIdx) => <div
                className="meta-duplicates-document"
                key={docIdx}
            >
                <div
                    title={doc.name}
                    className="meta-duplicates-document-name"
                    onClick={() => window.open(api.publicUrl + "/stack/" + (doc.stack ? doc.stack : "unclassified") + "/document/" + doc._id)}
                >{doc.name}</div>
                <div className="meta-duplicates-document-stack">{this.getStackPath(doc.stack)}</div>
                <div className="meta-duplicates-document-time">{formatDate(doc.receivedOn)}</div>
            </div>)}
        </> : <></>}
        

        {/* Split from another document? */}
        {doc.splitFrom ? <>
            {doc.original ? "" : <h1>Original</h1>}
            <p>
                This document was <strong>split</strong> from another larger document. See the <a href={api.publicUrl + "/document/" + doc.splitFrom}>original source document</a>.
            </p>
        </> : <></>}
    </>

    renderDocSharing = (user, team, doc, stack) => <>
        <h1>Sharing</h1>

        {/* Share with non-team users/outside accounts */}
        <div style={{ clear: 'both', height: 10 }} />
        <img
            alt="Share"
            className="meta-share-icon"
            src={"https://static.stax.ai/images/icons/" + (doc.sharing && doc.sharing.public ? "public" : (doc.sharing && doc.sharing.protected && doc.sharing.protected.password && doc.sharing.protected.password !== "" ? "lock" : "inherit")) + ".png"}
            title={doc.sharing && doc.sharing.public ? "Anyone with the link" : doc.sharing && doc.sharing.protected && doc.sharing.protected.password && doc.sharing.protected.password.length ? "Anyone with the password" : "Inherit stack sharing"}
        />

        {doc.sharing && (doc.sharing.public || (doc.sharing.protected && doc.sharing.protected.password && doc.sharing.protected.password.length)) ? <div className="meta-sharing-info"><a target="_blank" rel="noopener noreferrer" href={this.getPublicLink(doc)}>{this.getPublicLink(doc)}</a></div> : <div className="meta-sharing-info">Inherit stack access policy</div>}

        {user.isAdmin && <>
            <div
                className="meta-share-settings"
                onClick={(e) => this.openDocumentSharing(e)}
                onContextMenu={(e) => this.openDocumentSharing(e)}
            />
            <ContextMenu
                load={(open,close) => {
                    this.openDocumentSharing = open;
                    this.closeDocumentSharing = close;
                }}
                options={[
                    {
                        label: 'Inherit stack access policy',
                        action: () => this.props.updateDocument(doc._id, {
                            sharing: {
                                ...doc.sharing,
                                public: false,
                                users: [],
                                teams: []
                            }
                        })
                    },
                    {
                        label: 'Advanced sharing settings',
                        action: () => this.props.manageDocumentSharing(doc._id)
                    }
                ]}
            />
        </>}

        <div style={{ clear: 'both', height: 5 }} />
        <div className='meta-details-wrapper'>
        <div className="meta-details-label" style={{flexShrink:0}}>Assigned to</div>
        <Select
            className="select-container"
            classNamePrefix="select"
            value={(() => {
                if (!doc.assignee)
                    return { value: "none", label: "Select user..."};
                
                let assignedUser = team.users.find(u => u._id === doc.assignee);

                if (!assignedUser)
                    return { value: "none", label: "Select user..."};

                return {
                    value: assignedUser._id,
                    label: assignedUser.name
                };
            })()}
            options={[
                { value: "none", label: "None" },
                ...team.users.filter(u => u.name && u.name.length).map(u => ({
                    value: u._id,
                    label: u.name
                }))
            ]}
            isSearchable={true}
            placeholder="Select user..."
            onChange={async (option) => {
                await this.props.updateDocument(doc._id, { assignee: option.value === "none" ? null : option.value });
                if (option.value && option.value !== "none")
                    await this.props.notify(option.value, {
                        title: "You have been assigned!",
                        body: user.name + " assigned a document to you: " + doc.name + ".<br /><br /><a href=\"" + api.publicUrl + "/stack/" + doc.stack + "/document/" + doc._id + "\">Click here</a> to view."
                    });
            }}
        />
        </div>
        
    </>

    renderDocSync = (user, team, doc) => <>
        {
            !doc.sync || !doc.sync.length? <></>: 
            <>
                <h1>Local File</h1>
                {
                    doc.sync.map((sync, i) => {
                        const userIdx = team.users.findIndex(u => u._id === sync.user); 
                        if (userIdx < 0) return <React.Fragment key={i} />;
                        const user = team.users[userIdx]; 
                        return (
                            <>
                                {
                                    sync.error?
                                    <>
                                        <div
                                            className='meta-local-error'
                                        >
                                            <div
                                                className={`meta-history-log-icon palette-color-${userIdx%8}`}
                                                style={{
                                                    marginTop: 11,
                                                    left: 12
                                                }}
                                                title={user.name}
                                            >
                                                {user.name.substring(0,1)}
                                            </div>
                                            <div
                                                className='meta-local-path'
                                            >
                                                {sync.path.replace(/\\u0027/g, "'").replace(/\\u2019/g, '’')}
                                            </div>
                                            <div 
                                                className="meta-history-log-user meta-local meta-local-user-name" 
                                                title={user.name}
                                            >
                                                {user.name}
                                            </div>
                                            <div
                                                className='meta-local-error-msg'
                                            >
                                                {sync.error}
                                            </div>
                                        </div>
                                        <div className='meta-automate-actions'>
                                            <div
                                                className="meta-automate-retry"
                                                title="Unflag document and retry saving the document."
                                                onClick={() => this.retrySaving(doc, i)}
                                            >
                                                Retry saving
                                            </div>
                                        </div>
                                    </>:
                                    <>
                                        <div
                                            key={i}
                                            className="meta-history-log meta-local"
                                            title={sync.path.replace(/\\u0027/g, "'").replace(/\\u2019/g, '’')}
                                        >   
                                            <div
                                                className={`meta-history-log-icon palette-color-${userIdx%8}`}
                                                title={user.name}
                                            >
                                                {user.name.substring(0,1)}
                                            </div>
                                            {sync.path.replace(/\\u0027/g, "'").replace(/\\u2019/g, '’')}
                                            <div className="meta-history-log-user meta-local" title={user.name}>{user.name}</div>
                                            <div className="meta-local-status">
                                                {sync.isSynced ? 
                                                    <img src="https://static.stax.ai/images/icons/reviewed.png" alt="Synced" /> 
                                                    : 
                                                    <Loading
                                                        type="spin"
                                                        color="var(--color-primary)"
                                                        height={24}
                                                        width={24}
                                                    />
                                                }
                                            </div>
                                        </div>
                                    </>
                                }
                            </>
                        )
                    })
                }
            </>
        }
    </>

    renderDocForms = (user, team, doc, forms) => <>
        {forms && forms.length > 0 ? <>
            <h1>Forms</h1>

            {forms.map((form,idx) => {
                const f = this.props.forms.find(f => f._id === form);
                if (!f) return <></>;

                let totalFields = 0;
                let completedFields = 0;

                if (f.sections)
                    for (const section of f.sections)
                        for (const field of section.fields) {
                            if (!field.required) continue;
                            if (doc.metadata.find(f => f.key === field.key))
                                completedFields += 1;
                            totalFields += 1;
                        }

                const percentComplete = 100 * completedFields / (totalFields ? totalFields : 1);

                return <div
                    className="form-field-list"
                    key={idx}
                    onClick={() => this.props.modal("form-field", {
                        form: f,
                        metadata: doc.metadata,
                        docId: doc._id
                    })}
                >
                    <div className="form-field-list-progress">
                        <CircularProgressbar
                            value={percentComplete}
                            text=""
                            strokeWidth={8}
                            styles={buildStyles({

                                textSize: '28px',
                                trainColor: 'rgb(230,230,230)',
                                pathColor: 'var(--color-primary)'
                            })}
                        />
                        {percentComplete === 100 ? <div className="form-field-list-complete" /> : ""}
                    </div>
                    <div className="form-field-list-name">{f.title}</div>
                </div>;
            })}

        </> : ""}
    </>

    renderDocAttachments = (user, team, doc, stack) => <>
        {doc.attachments && doc.attachments.length > 0 && <>
            {/* Attachments */}
            <h1>Attachments</h1>
            {doc.attachments.map((file,idx) => {
                if (!file.filename || file.filename === "") return <></>;

                const filename = file.filename;
                const filenameCmp = filename.split(".");
                let extension = filenameCmp[filenameCmp.length-1];

                if (!["7z", "csv", "doc", "docx", "gz", "jpeg", "jpg", "odt", "pdf", "png", "ppt", "pptx", "rtf", "tif", "tiff", "txt", "xls", "xlsx", "zip"].includes(extension))
                    extension = "attachment";

                return (<div
                    key={idx}
                    className="meta-attachment-file"
                    style={{
                        backgroundImage: "url('https://static.stax.ai/images/format/" + extension + ".png')"
                    }}
                >
                    <div
                        className="meta-attachment-filename"
                        title={filename}
                        onClick={async () => {
                            if (file.document)
                                return window.open("/stack/unread/document/" + file.document, "_blank");

                            await this.props.loading(1005);
                            const res = await api.download('/document/downloadAttachment?docId=' + doc._id + '&filepath=' + encodeURIComponent(file.filepath));
                            download(res, filename);
                            this.props.loaded();
                        }}
                    >{filename}</div>
                    <img
                        className="metadata-edit-delete"
                        src="https://static.stax.ai/images/icons/remove.png"
                        alt="(-)"
                        title="Remove attachment"
                        onClick={() => {
                            let attachments = doc.attachments;
                            attachments.splice(idx,1);
                            this.props.updateDocument(doc._id, {
                                attachments
                            }, true);
                        }}
                    />
                </div>);
            })}
        </>}

        {/* Attach file */}
        {/* <div
            className="meta-action"
            title="Upload a new file to attach"
            style={{
                backgroundImage: "url('https://static.stax.ai/images/icons/action-upload.png",
                marginTop: 3
            }}
            onClick={this.attachFile}
        >Attach file</div> */}

        {/* Attach document in Stax·ai */}
        {/* <div
            className="meta-action"
            title="Attach a document from Stax·ai"
            style={{
                backgroundImage: "url('https://static.stax.ai/images/icons/action-attach.png"
            }}
            onClick={() => this.props.modal("link-document")}
        >Link document</div> */}
    </>

    renderDocTables = (user, team, doc, stack) => {

        if(!doc.tables){
            return <></>;
        } 

        // Sort the tables so any with a name, are at the top. 
        doc.tables.sort((a, b) => {
            if(a.title?.toLowerCase().includes('transaction') && !(b.title?.toLowerCase().includes('transaction'))) return -1;
            if(b.title?.toLowerCase().includes('transaction') && !(a.title?.toLowerCase().includes('transaction'))) return 1;
            if(a.title && !b.title) return -1;
            if(b.title && !a.title) return 1;
            return 0;
        }); 

        const tables = this.state.showAllTables? doc.tables: doc.tables.slice(0, 3); 
        
        return (<>
            <h1>Tables</h1>
            
            {tables.map((table, idx) => {
                const defaultTableTitle = () => {
                    const tables = doc.tables.filter(t => t.page === table.page);
                    return `Page ${table.page + 1} - Table ${tables.findIndex(t => t === table) + 1} (auto)`;
                }
                return (
                    <>
                        {this.props.editTableIdx !== idx?   
                            <div
                                className="meta-table-selection-container"
                                key={`table-${defaultTableTitle()}`}
                            >
                                <div 
                                    className="meta-table-img"
                                    key={`table-img-${idx}`}
                                    style={{
                                        backgroundImage: "url('https://static.stax.ai/images/format/xlsx.png')"
                                    }}
                                >

                                    {/* Number of tags */}
                                    {table.tags?.length > 1?
                                        <div
                                            className='metadata-edit-table-tag-count'
                                        >
                                            {table.tags.length}
                                        </div>
                                        :''
                                    }
                                </div>
                                <div
                                        className="meta-table-title"
                                        key={`table-title-${idx}`}
                                        title={table.title?.length > 0? table.title: defaultTableTitle()}
                                        onClick={() => {
                                            this.setState({newTableTitle: table.title?.length > 0? table.title: defaultTableTitle(), createNewTable: false});
                                            this.props.setEditTableIdx(idx);

                                            setTimeout(() => { // auto scroll to the page 
                                                const pageDiv = document.getElementById('page-' + String(table.page));
                                                if (pageDiv) pageDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                            }, 100);
                                        }}
                                    >   
                                        {/* Does the table have a tag? */}
                                        {table.tags?.length > 0?
                                            <img
                                                className="meta-details-tag"
                                                src="https://static.stax.ai/images/icons/tag.png"
                                                alt="[TAG]"
                                            />
                                            : 
                                            ''
                                        }
                                        
                                        {table.title?.length > 0? table.title: defaultTableTitle()}
                                    </div>
                            </div>
                            :
                            <div
                                className="meta-sign-container meta-sign-container-expanded"
                                key={`table-${idx}-sign-container-expanded`}
                                style={{
                                    marginBottom: 14
                                }}
                            >
                                <div
                                    className="meta-sign-container-title"
                                    onClick={(e) => {
                                        if(e.target.className === 'metadata-delete-icon')
                                            return 
                                        this.props.setEditTableIdx(-1);
                                    }}
                                >
                                    {table.title?.length > 0? table.title: defaultTableTitle()}
                                    {` (${table.rows?.length} rows)`} {/* Title of the table */}
                                    <img 
                                        src="https://static.stax.ai/images/icons/delete.png" 
                                        className="metadata-delete-icon" 
                                        title="Click to delete the table"
                                        alt="Delete"
                                        onClick={() => this.props.modal("confirm", {
                                            message: <>
                                                If you delete this table, you will <strong>permanently lose all table tags and populated cell values</strong>. This table will not be used when documents are looking for similar tables.
                                            </>,
                                            action: () => {
                                                const tables = doc.tables; 
                                                tables.splice(idx, 1);
                                                this.props.setEditTableIdx(-1);
                                                this.props.updateDocument(doc._id, {tables}, true);
                                            }
                                        })}
                                    />
                                </div>
                                <div
                                    className="meta-sign-config"
                                >
                                    <div className="meta-details-label meta-sign-label">Title</div>
                                    <div className="meta-details-value meta-sign-value meta-sign-value-select">
                                        <input
                                            id={"table-title"}
                                            name={"table-title"}
                                            key={"table-title-editor"}
                                            type="text"
                                            className="metadata-edit-table-title"
                                            value={this.state.newTableTitle}
                                            placeholder={table.title?? defaultTableTitle()}
                                            onChange={e => this.setState({ newTableTitle: e.target.value })}
                                            onKeyDown={(e) => inputKey(e, {
                                                "Enter": () => {
                                                    document.getElementById('table-title').blur();
                                                    // has the title changed?
                                                    if (this.state.newTableTitle !== table.title) {
                                                        const tables = doc.tables;
                                                        tables[idx].title = this.state.newTableTitle;
                                                        this.props.updateDocument(doc._id, {tables}, true);
                                                    }
                                                }
                                            })}
                                        />
                                    </div>
                                    {table.changed && 
                                        <div 
                                            className="a" 
                                            style={{marginBottom: -15}}
                                            onClick={() => {
                                                if((!this.props.statement?.confirmed && this.props.user.email.endsWith("@stax.ai")) || (!this.props.statement?.clientConfrimed && !this.props.user.email.endsWith("@stax.ai"))){
                                                    const tables = doc.tables; 
                                                    tables[idx].changed = false; 
                                                    this.props.updateDocument(doc._id, {tables}, true);
                                                }
                                                else{
                                                toast.error("You cannot make changes to an approved statement")
                                                } 
                                            }}
                                        >
                                            Save Changes
                                        </div>
                                    }
                                </div>
                                
                                {table.tags?.length > 0 ?
                                <>
                                     <h1
                                        style={{
                                            height: 'auto',
                                            padding: 0,
                                            paddingLeft: '10px',
                                            lineHeight: 0,
                                        }}
                                    >Tagged Cells</h1>

                                    {table.tags.map((tag, tagIdx) => {
                                        const HEADERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

                                        const toggleTagHighlight = () => {
                                            const id = `table-editor-${tag.row}-${tag.col}`;
                                            const element = document.getElementById(id);
                                            if(!element)
                                                return 
                                            element.classList.toggle('hover-tag')
                                        }

                                        return (
                                            <div    
                                                className='metadata-edit-table-tag-container'
                                            >
                                                <div
                                                    className='metadata-edit-table-tag'
                                                    onMouseOver={toggleTagHighlight}
                                                    onMouseLeave={toggleTagHighlight}
                                                >
                                                    {tag.row === -1? "TABLE": `${HEADERS.charAt(tag.col)} ${tag.row + 1}`}
                                                </div>

                                                <div className="metadata-edit-table-tag-value">
                                                    <CreatableSelect
                                                        classNamePrefix="metadata-select"
                                                        className="metadata-edit-table-tag-value-select"
                                                        style={{color: 'white !important'}}
                                                        value={{
                                                            value: tag.key,
                                                            label: tag.key.toUpperCase()
                                                        }}
                                                        options={stack?.keys?.map(key => ({
                                                            value: key,
                                                            label: key.toUpperCase()
                                                        })) ?? []}
                                                        isSearchable={true}
                                                        placeholder="Select field key"
                                                        onChange={async (option) => {
                                                            const tables = doc.tables;
                                                            tables[idx].tags[tagIdx].key = option.value;

                                                            const metadata = this.props.document.metadata;
                                                            if(tag.row >= 0){
                                                                // Find the first word that fits within the bounds of the 
                                                                const cell = tables[idx].rows[tag.row].cells[tag.col];

                                                                const words = this.props.document.words
                                                                    .filter(w => w.page === tables[idx].rows[tag.row].page)
                                                                    .reduce((acc, w, wIdx) => {
                                                                    // Calculate overlap
                                                                    const overlap = Math.max(0, Math.min(cell.x + cell.width, w.x + w.width) - Math.max(cell.x, w.x)) *
                                                                                    Math.max(0, Math.min(cell.y + cell.height, w.y + w.height) - Math.max(cell.y, w.y)) / 
                                                                                    (w.width * w.height);
                                                                
                                                                    // Check if overlap is sufficient and add the index if so
                                                                    if (overlap > 0.7) {
                                                                        acc.push(wIdx);
                                                                    }
                                                                
                                                                    return acc;
                                                                }, []);
                                                                
                                                                // Does this field already exist? 
                                                                const fieldIdx = metadata.findIndex(f => f.key === option.value);
                                                                const field ={
                                                                    key: option.value,
                                                                    value: tables[idx].rows[tag.row].cells[tag.col].text,
                                                                    pages: [tables[idx].rows[tag.row].page],
                                                                    tags: words,
                                                                    ref: tables[idx]?.rows[0]?.cells[tag.col]?.text,
                                                                    confidence: 1
                                                            }
                                                                if(fieldIdx >= 0){
                                                                    metadata[fieldIdx] = field;
                                                                }else 
                                                                    metadata.push(field);
                                                            }
                                                            await this.props.updateDocument(doc._id, {tables, metadata}, true);
                                                        }}
                                                    />
                                                </div>

                                                <img
                                                    className="metadata-table-tag-delete"
                                                    src="https://static.stax.ai/images/icons/remove.png"
                                                    alt="(-)"
                                                    title="Delete metadata field"
                                                    onClick={() => {
                                                        const tables = doc.tables;
                                                        tables[idx].tags.splice(tagIdx, 1);
                                                        this.props.updateDocument(doc._id, {tables}, true);
                                                        toggleTagHighlight(); 
                                                    }}
                                                    onMouseOver={toggleTagHighlight}
                                                    onMouseLeave={toggleTagHighlight}
                                                />
                                            </div>
                                        )
                                    })}
                                </>
                                    :
                                    ""
                                }
                            </div>
                        }
                    </>
                );
            })}
            {
                doc.tables.length > 3 && 
                <div
                    className='meta-showmore'
                    onClick={() => this.setState({showAllTables: !this.state.showAllTables})}
                >
                    {this.state.showAllTables? "Show fewer tables...": "Show more tables..."}
                </div>
            }

            {/* Create a new Table */}
            {this.state.createNewTable? 
                <div
                    className={"meta-sign-container meta-sign-container-expanded"}
                    key={"create-new-table"}
                >
                    <div
                        className="meta-sign-container-title"
                    >
                        Configure Table
                        <img
                            src="https://static.stax.ai/images/icons/delete.png"
                            alt="(-)"
                            title="Edit the table configuration"
                            onClick={(e) => {
                                this.setState({ createNewTable: false });
                            }}
                        />
                    </div>
                    <div className="meta-sign-config">
                        <div className="meta-details-label meta-sign-label">Title</div>
                        <div className="meta-details-value meta-sign-value meta-sign-value-select">
                            <input
                                id={"table-title"}
                                name={"table-title"}
                                key={"table-title-editor"}
                                type="text"
                                className="metadata-edit-table-title"
                                value={this.state.newTableTitle}
                                onChange={e => this.setState({ newTableTitle: e.target.value })}
                                onKeyDown={(e) => inputKey(e, {
                                    "Enter": () => {document.getElementById('table-title').blur()}
                                })}
                            />
                        </div>


                        <div className="meta-details-label meta-sign-label">Rows</div>
                        <div className="meta-details-value meta-sign-value meta-sign-value-select">
                            <CreatableSelect
                                className="metadata-select-container"
                                classNamePrefix="metadata-select"
                                value={{
                                    value: this.state.newTableRows,
                                    label: this.state.newTableRows.toString()
                                }}
                                options={ new Array(25).fill(0).map((_,idx) => {
                                    return {
                                        value: idx+2,
                                        label: (idx+2).toString()
                                    }
                                })}
                                onChange = {(e) => {
                                    this.setState({newTableRows: e.value});
                                }}
                                isSearchable={false}
                                placeholder="Select number of rows"
                            />
                        </div>

                        <div className="meta-details-label meta-sign-label">Columns</div>
                        <div className="meta-details-value meta-sign-value meta-sign-value-select">
                            <CreatableSelect
                                className="metadata-select-container"
                                classNamePrefix="metadata-select"
                                value={{
                                    value: this.state.newTableCols,
                                    label: this.state.newTableCols.toString()
                                }}
                                options={ new Array(25).fill(0).map((_,idx) => {
                                    return {
                                        value: idx+2,
                                        label: (idx+2).toString()
                                    }
                                })}
                                onChange = {(e) => {
                                    this.setState({newTableCols: e.value});
                                }}
                                isSearchable={false}
                                placeholder="Select number of cols"
                            />
                        </div>
                        
                        <div className="meta-details-label meta-sign-label">Location</div>
                        <div className="meta-details-value meta-sign-value">
                            {this.state.editNewTableBounds? 
                                <div
                                    key={this.state.newTableIdx}
                                    className="meta-details-bound meta-details-bound-edit"
                                    onClick={() => {
                                        this.setState({
                                            editNewTableBounds: false
                                        });
                                        this.props.setFunction();
                                    }}
                                >Click here to cancel</div>
                                :
                                <div
                                className="meta-details-bound meta-details-bound-notset"
                                onClick={() => {
                                    this.setState({editNewTableBounds: true})
                                    this.props.setFunction("draw-input-bounds", {
                                        callback: async(page,x,y,width,height) => {
                                            const bounds = {
                                                page,
                                                x,
                                                y,
                                                width,
                                                height
                                            }
                                            let tables = doc.tables; 

                                            let table = {
                                                ...bounds,
                                                raw: false, 
                                                title: this.state.newTableTitle,
                                                rows:[], 
                                                headers: new Array(this.state.newTableCols).fill('')
                                            }
                                            new Array(this.state.newTableRows).fill(0).map((_, rdx) => {
                                                const row = {
                                                    x: bounds.x,
                                                    y: bounds.y + (((bounds.height / this.state.newTableRows) * rdx)),
                                                    width: bounds.width,
                                                    height: bounds.height / this.state.newTableRows,
                                                    page: bounds.page,
                                                    cells: []
                                                }
                                                table.rows.push(row);
                                                return _;
                                            })
                                            
                                            table.rows.map((row, rdx) => {
                                                new Array(this.state.newTableCols).fill(0).map((_, cdx) => {
                                                    const col = {
                                                        x: row.x + ((row.width / this.state.newTableCols) * cdx),
                                                        y: row.y,
                                                        width: row.width / this.state.newTableCols,
                                                        height: row.height,
                                                        text: ""
                                                    }
                                                    row.cells.push(col);
                                                    return _;
                                                })
                                                return row;
                                            });

                                            // Check if the page has a rotation, if so, rotate the table opposite of the page rotation 
                                            const rotations = {...this.props.document?.rotations??{}};
                                            Object.keys(rotations).forEach(page => {
                                                rotations[page] = 360 - rotations[page];
                                            });

                                            table = rotateTable(table, rotations);
                                            
                                            tables.push(table);
                                            this.props.setFunction();                                   
                                            this.props.updateDocument(doc._id, {
                                                tables
                                            }, true, true);
                                            this.setState({createNewTable: false, editNewTableBounds: false});
                                            //this.props.setEditableTableIdx1);
                                            this.props.setEditTableIdx(tables.length - 1);
                                        }
                                    })
                                }}
                            >Add location.</div>
                            }
                        </div>
                    </div>
                    <div style={{ clear: 'both' }} />
                </div>
                :
                <div
                    className="meta-action"
                    title="Add a new table to the document"
                    style={{
                        backgroundImage: "url('https://static.stax.ai/images/icons/action-create.png",
                        marginTop: 3
                    }}
                    onClick={() => {
                        this.setState({createNewTable: true, newTableTitle: ""})
                        this.props.setEditTableIdx(-1);
                        // this.setState({newTableIdx: doc.tables.length});
                        // this.props.setEditableTableIdx(doc.tables.length);   
                    }}
                >New table</div> 
            }
        </>);
    }

    renderDocFillable = (user, team, doc, stack) => {
        // Fillable inputs - compiled
        let fillable = [];
        if (doc.fillable && doc.fillable.enabled && doc.fillable.fields && doc.fillable.fields.length) {
            for (const [idx, field] of doc.fillable.fields.entries()) {
                if (fillable.map(f => f.key).includes(field.key)) {
                    const fIdx = fillable.findIndex(f => f.key === field.key);
                    fillable[fIdx].fields.push({
                        ...field,
                        idx
                    });
                }
                else fillable.push({
                    key: field.key,
                    dataType: field.dataType,
                    fields: [ { ...field, idx } ]
                });
            }
        }

        return <>
            {doc.fillable && doc.fillable.enabled && <>
                {/* Fillable form configuration */}
                <h1>Fill & Sign</h1>

                <p style={{ marginBottom: 15 }}>
                    Set up this document to allow users to fill and sign.
                </p>

                {fillable.length ? fillable.map((group,idx) => <div
                    className={"meta-sign-container" + (this.state.editInput === idx ? " meta-sign-container-expanded" : "")}
                    key={idx}
                >
                    <div
                        className="meta-sign-container-title"
                        onClick={() => this.setState({ editInput: this.state.editInput === idx ? -1 : idx })}
                    >
                        {this.state.editInput === idx ? "Configure Input" : group.key && group.key !== "" ? <>{group.key.toUpperCase()}<span style={{
                            marginLeft: 10,
                            fontWeight: 400,
                            lineHeight: '20px',
                            color: 'rgb(180,180,180)'
                        }}>({capitalize(group.dataType)})</span></> : "Unnamed Input"}
                        {this.state.editInput === idx ? <img
                            src="https://static.stax.ai/images/icons/delete.png"
                            alt="(-)"
                            title="Remove input field"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                let fields = [ ...doc.fillable.fields ];
                                let fIdx = fields.findIndex(f => f.key === group.key);
                                while (fIdx >= 0) {
                                    fields.splice(fIdx,1);
                                    fIdx = fields.findIndex(f => f.key === group.key);
                                }
                                this.props.updateDocument(doc._id, {
                                    fillable: {
                                        ...doc.fillable,
                                        fields
                                    }
                                });
                            }}
                        /> : ""}
                    </div>

                    {this.state.editInput === idx ? <div className="meta-sign-config">
                        <div className="meta-details-label meta-sign-label">Field key</div>
                        <div className="meta-details-value meta-sign-value meta-sign-value-select">
                            <CreatableSelect
                                className="metadata-select-container"
                                classNamePrefix="metadata-select"
                                value={{
                                    value: group.key,
                                    label: group.key ? group.key.toUpperCase() : ""
                                }}
                                options={stack && stack.keys && stack.keys.length > 0 ? stack.keys.map(key => ({
                                    value: key,
                                    label: key.toUpperCase()
                                })) : []}
                                isSearchable={true}
                                placeholder="Select Key"
                                onChange={async (option) => this.editFillableFieldGroup(doc, group.key, {
                                    key: option && option.value ? option.value.toLowerCase() : ""
                                })}
                            />
                        </div>

                        <div className="meta-details-label meta-sign-label">Field type</div>
                        <div className="meta-details-value meta-sign-value meta-sign-value-select">
                            <Select
                                className="metadata-select-container"
                                classNamePrefix="metadata-select"
                                value={{
                                    value: group.dataType,
                                    label: group.dataType in OPTS_SIGN_TYPE ? OPTS_SIGN_TYPE[group.dataType] : group.dataType.toUpperCase()
                                }}
                                options={Object.keys(OPTS_SIGN_TYPE).map(option => ({
                                    value: option,
                                    label: OPTS_SIGN_TYPE[option]
                                }))}
                                isSearchable={false}
                                placeholder="Select input type"
                                onChange={async (option) => this.editFillableFieldGroup(doc, group.key, {
                                    dataType: option.value
                                })}
                            />
                        </div>

                        {/* {field.dataType === "today" ? "" : <>

                            <div className="meta-details-label meta-sign-label">Skip input</div>
                            <div className="meta-details-value meta-sign-value meta-sign-value-select">
                                <Select
                                    className="metadata-select-container"
                                    classNamePrefix="metadata-select"
                                    value={{
                                        value: field.required === true ? "No, input is required." : "Yes, input is optional.",
                                        label: field.required === true ? "No, input is required." : "Yes, input is optional."
                                    }}
                                    options={["No, input is required.", "Yes, input is optional."].map(option => ({
                                        value: option,
                                        label: option
                                    }))}
                                    isSearchable={false}
                                    placeholder="Select an option"
                                    onChange={async (option) => this.editFillableField(doc, idx, {
                                        required: option.value === "No, input is required."
                                    })}
                                />
                            </div>

                            <div className="meta-details-label meta-sign-label">Input Hint</div>
                            <div className="meta-details-value meta-sign-value">
                                <Editable
                                    className="meta-details-config"
                                    containerClass="meta-details-config-container"
                                    inputClass="meta-details-config-edit"
                                    valueClass="meta-details-config-value"
                                    fieldName="Instructions for person filling the form."
                                    value={field.hint}
                                    editable={true}
                                    setValue={(value) => this.editFillableField(doc, idx, {
                                        hint: value
                                    })}
                                    placeholder="(Optional)"
                                />
                            </div>
                        </>} */}

                        <div className="meta-details-label meta-sign-label">Locations</div>
                        <div className="meta-details-value meta-sign-value">
                            {group.fields && group.fields.length ? group.fields.map((field,jdx) => this.state.editInputBounds === field.idx ?
                            // Currently editing bounds
                            <div
                                key={jdx}
                                className="meta-details-bound meta-details-bound-edit"
                                onClick={() => {
                                    this.setState({
                                        editInputBounds: null
                                    });
                                    this.props.setFunction();
                                }}
                            >Click here to cancel.</div> :

                            // Bounds not set
                            !field.x || !field.y || !field.width || !field.height ?
                            <div
                                key={jdx}
                                className="meta-details-bound meta-details-bound-notset"
                                onClick={() => this.setInputBounds(doc, field)}
                            >Add location.</div> :

                            // Bounds set - only location. Reset?
                            group.fields.length === 1 ?
                            <div
                                key={jdx}
                                className="meta-details-bound meta-details-bound-set"
                                title={"See location of field input on page " + String(field.page+1)}
                                onClick={async () => {
                                    await this.editFillableField(doc, field.idx, {
                                        page: -1,
                                        x: -1,
                                        y: -1,
                                        width: 0,
                                        height: 0
                                    }, false);
                                    this.setInputBounds(doc, field);
                                }}
                                onMouseEnter={() => this.props.hover({ input: field })}
                                onMouseLeave={() => this.props.hover(null)}
                            >Set. Click to reset.</div> :

                            // Multiple locations are available - click to remove
                            <div
                                key={jdx}
                                className="meta-details-bound meta-details-bound-set"
                                title={"See location of field input on page " + String(field.page+1)}
                                onClick={async () => {
                                    let fields = [ ...doc.fillable.fields ];
                                    fields.splice(field.idx,1);
                                    await this.props.updateDocument(doc._id, {
                                        fillable: {
                                            ...doc.fillable,
                                            fields
                                        }
                                    });
                                }}
                                onMouseEnter={() => this.props.hover({ input: field })}
                                onMouseLeave={() => this.props.hover(null)}
                            >Set. Click to remove.</div>) : ""}

                            {group.fields.reduce((s, f) => s + (f.x && f.y && f.width && f.height ? 1 : 0), 0) === group.fields.length ?
                            // Add a new field location?
                            <div
                                className="meta-details-bound meta-details-bound-notset"
                                onClick={async () => {
                                    const idx = doc.fillable.fields.length;

                                    const newField = {
                                        key: group.key,
                                        required: false,
                                        hint: "",
                                        options: [],
                                        dataType: group.dataType
                                    };

                                    // Add to document
                                    await this.props.updateDocument(doc._id, {
                                        fillable: {
                                            ...doc.fillable,
                                            fields: [
                                                ...doc.fillable.fields,
                                                newField
                                            ]
                                        }
                                    }, false);

                                    // Start editing location
                                    this.setInputBounds(doc, { ...newField, idx });
                                }}
                            >Add location.</div> : ""}
                        </div>

                    </div> : ""}

                    <div style={{ clear: 'both' }} />
                </div>) : ""}

                <div style={{ clear: 'both' }} />

                <div
                    className="meta-action"
                    title="Add a new fillable field"
                    style={{
                        backgroundImage: "url('https://static.stax.ai/images/icons/action-create.png",
                        marginTop: 3,
                        clear: 'both'
                    }}
                    onClick={() => {
                        this.setState({
                            editInput: fillable.length
                        });
                        this.props.updateDocument(doc._id, {
                            fillable: {
                                ...doc.fillable,
                                fields: [
                                    ...doc.fillable.fields,
                                    {
                                        key: "",
                                        required: false,
                                        hint: "",
                                        options: [],
                                        dataType: "textbox"
                                    }
                                ]
                            }
                        });
                    }}
                >New input</div>

                {doc.fillable.recipients && doc.fillable.recipients.length ? <>
                    <p style={{ marginTop: 15, marginBottom: 15 }}>
                        You can track when your recipients fill and sign their copies of this document below.
                    </p>

                    {true || doc.fillable.recipients.filter(r => r.filledOn).length ? <div
                        className="document-fillable-clear-all"
                        title="Clear all filled recipients"
                        onClick={async () => {
                            let recipients = [ ...doc.fillable.recipients ];
                            let rIdx = recipients.findIndex(r => r.filledOn);
                            while (rIdx >= 0) {
                                recipients.splice(rIdx,1);
                                rIdx = recipients.findIndex(r => r.filledOn);
                            }
                            await this.props.updateDocument(doc._id, {
                                fillable: {
                                    ...doc.fillable,
                                    recipients
                                }
                            });
                        }}
                    >Clear filled recipients...</div> : ""}

                    {doc.fillable.recipients.map((r,idx) => <div
                        key={idx}
                        className={"document-fillable-recipient" + (r.filledOn && r.document ? " document-fillable-recipient-filled" : "")}
                    >
                        <h1
                            title={r.filledOn && r.document ? "Click to open document" : ""}
                            onClick={() => r.filledOn && r.document ? this.props.history.push("/stack/" + (doc.stack ? doc.stack : "unclassified") + "/document/" + r.document) : {}}
                        >{r.email}</h1>

                        <div
                            className="document-fillable-recipient-clear"
                            title="Clear recipient workflow"
                            onClick={async () => {
                                let recipients = [ ...doc.fillable.recipients ];
                                recipients.splice(idx,1);
                                await this.props.updateDocument(doc._id, {
                                    fillable: {
                                        ...doc.fillable,
                                        recipients
                                    }
                                });
                            }}
                        />

                        <div
                            className="document-fillable-status-icon document-fillable-status-icon-done"
                            style={{ marginLeft: 10 }}
                        />
                        <div className="document-fillable-status-icon-connector-left document-fillable-status-icon-connector document-fillable-status-icon-connector-done" />
                        <div className={"document-fillable-status-icon-connector-right document-fillable-status-icon-connector" + (r.viewedOn ? " document-fillable-status-icon-connector-done" : "")} />
                        <div className={"document-fillable-status-icon" + (r.viewedOn ? " document-fillable-status-icon-done" : "")} />
                        <div className={"document-fillable-status-icon-connector-left document-fillable-status-icon-connector" + (r.viewedOn ? " document-fillable-status-icon-connector-done" : "")} />
                        <div className={"document-fillable-status-icon-connector-right document-fillable-status-icon-connector" + (r.filledOn ? " document-fillable-status-icon-connector-done" : "")} />
                        <div className={"document-fillable-status-icon" + (r.filledOn ? " document-fillable-status-icon-done" : "")} />
                        <div style={{ clear: 'both', height: 5 }} />

                        <div
                            className="document-fillable-status-title document-fillable-status-title-done"
                            style={{ left: 2 }}
                        >Sent</div>
                        <div
                            className={"document-fillable-status-title" + (r.viewedOn ? " document-fillable-status-title-done" : "")}
                            style={{ left: 112 }}
                        >Viewed</div>
                        <div
                            className={"document-fillable-status-title" + (r.filledOn ? " document-fillable-status-title-done" : "")}
                            style={{ left: 224 }}
                        >Filled</div>

                        <div
                            className="document-fillable-status-timestamp document-fillable-status-timestamp-done"
                            style={{ left: 2 }}
                        >{r.sentOn ? formatDate(r.sentOn) : "-"}</div>
                        {r.viewedOn ? <div
                            className="document-fillable-status-timestamp"
                            style={{ left: 112 }}
                        >{formatDate(r.viewedOn)}</div> : ""}
                        {r.filledOn ? <div
                            className="document-fillable-status-timestamp"
                            style={{ left: 224 }}
                        >{formatDate(r.filledOn)}</div> : ""}
                    </div>)}
                </> : ""}
            </>}
        </>;
    }

    render = () => {
        const user = this.props.user && this.props.user._id ? this.props.user : { _id: null, isAdmin: false };
        const team = this.props.team && this.props.team._id ? this.props.team : { _id: null, users: [], stacks: [] };

        const stack = this.props.stack ? this.props.stack : {
            trainer: {}
        };

        //Add 'assignee' and 'uploaded by' to the list of keys
        const filterKeys = [ '🛑 confidence', '👩‍💻 assignee', '📤 uploaded by', '🚩 flagged', '⚠️ error message', ...stack?.keys ?? []];
        
        if (!stack.trainer) stack.trainer = {};
        
        let sel = this.props.selection ? this.props.selection : [];
        let forms = [];

        const doc = window.location.href.includes("/document/") ? this.props.document : this.getSelectedDocument();

        if (!doc)
            sel = [];
        else {
            forms = this.getStackForms(doc.stack);
        }

        const isPseudoStack = !window.location.href.includes("/stack/") || (stack && stack._id && stack._id.length !== 24);
        const actionsConfig = this.getActions();
        const actions = (
            sel.length > 1 ? actionsConfig.multiple :
            sel.length > 0 ? actionsConfig.single :
            window.location.href.includes("/field/") ? actionsConfig.field :
            actionsConfig.stack
        ).filter(action => (!action.noPseudo || !isPseudoStack) && (typeof action.disabled !== "function" || !action.disabled()));

        const uploadedBy = doc && doc.source === "Uploaded" ? this.getUserName(team.users, doc.provider) : "";

        // Does this stack have any documents with the metadata key: "event date"?
        const allowTimelineView = this.props.docs?.filter(d => d?.metadata?.filter(m => m.key === "event date")?.length > 0)?.length > 0;

        return (<div className="meta-container">
    
            <div className="meta-details">

                {sel.length > 1 ?

                // Multiple documents selected
                <>
                    <h1>Fields</h1>
                    <p>Set field for all selected documents.</p>

                    <input
                        id={"key-selection"}
                        name={"key-selection"}
                        type="text"
                        className="metadata-edit-key"
                        value={this.state.selectionMetaKey}
                        onChange={e => this.setState({ selectionMetaKey: e.target.value })}
                    /> : 
                    <input
                        id={"value-selection"}
                        name={"value-selection"}
                        type="text"
                        className="metadata-edit-value"
                        value={this.state.selectionMetaValue}
                        onChange={e => this.setState({ selectionMetaValue: e.target.value })}
                        onKeyDown={(e) => inputKey(e, {
                            "Enter": this.setSelectionMeta
                        })}
                    />

                    <img
                        className="metadata-edit-delete"
                        src="https://static.stax.ai/images/icons/next-circle.png"
                        alt="(->)"
                        title="Set field for selected documents"
                        onClick={this.setSelectionMeta}
                    />

                    <h1>Sharing</h1>
                    <div>
                        <div style={{ clear: 'both'}} />
                        <div className='meta-details-wrapper'>
                        <div className="meta-details-label" style={{flexShrink:0}}>Assigned to</div>    
                            <Select
                                className="select-container"
                                classNamePrefix="select"
                                value={(() => {
                                    const assignedUsers = this.props.docs.filter(d => sel.includes(d._id)).map(d => d.assignee) 

                                    if (assignedUsers.every(val => val === null || val === undefined)) return { value: "none", label: "Select user..."};

                                    const assignedUser = assignedUsers.every(val => val === assignedUsers[0]) ? team.users.find(u => u._id === assignedUsers[0]) : null;

                                    if (!assignedUser)
                                        return { value: "none", label: "Multiple users..."};

                                    return {
                                        value: assignedUser._id,
                                        label: assignedUser.name
                                    };

                                })()}
                                options={[
                                    { value: "none", label: "None" },
                                    ...team.users.filter(u => u.name && u.name.length).map(u => ({
                                        value: u._id,
                                        label: u.name
                                    }))
                                ]}
                                isSearchable={true}
                                placeholder="Select user..."
                                onChange={async (option) => {
                                    await this.props.updateDocuments(sel, { assignee: option.value === "none" ? null : option.value });
                                    if (option.value && option.value !== "none") {
                                        await this.props.notify(option.value, {
                                            title: "You have been assigned!",
                                            body: user.name + " assigned multiple documents to you: <br />" + this.props.docs.filter(d => sel.includes(d._id)).map(d => `<a href=${api.publicUrl + '/stack/' + d.stack + '/document/' + d._id}>${d.name}</a>`).join("<br />")
                                        });
                                    }
                                }}
                            />
                        </div>
                    </div>
                </> :

                sel.length === 1 ?

                // Single document selected
                <>

                    <h1>Details</h1>
                    <div className='meta-details-wrapper'>


                    <div className="meta-details-label">File name</div>
                    <div className="meta-details-value">
                        <Editable
                            className="meta-details-config"
                            containerClass="meta-details-config-container"
                            inputClass="meta-details-config-edit"
                            valueClass="meta-details-config-value"
                            fieldName="Document file name"
                            value={doc.name}
                            editable={true}
                            setValue={(value) => this.props.updateDocument(doc._id, { name: value }, true)}
                        />
                    </div>
                    </div>

                    <div className='meta-details-wrapper'>
                    <div className="meta-details-label">Location</div>
                    <div className="meta-details-value" title={this.getStackPath(doc.stack)}><a href={"/stack/" + (doc.stack ? doc.stack : "unclassified")}>{this.getStackPath(doc.stack)}</a></div>

                    </div>
                    
                    <div className='meta-details-wrapper'>
                    <div className="meta-details-label"># Pages</div>
                    <div className="meta-details-value">{doc.pages.length} {plural('page', doc.pages.length)}</div>


                    </div>
                    
                    <div className='meta-details-wrapper'>
                    
                    <div className="meta-details-label">Source</div>
                    <div className="meta-details-value" title={doc.source}>
                        <img
                            src={getSourceIcon(doc.source ?? "")}
                            alt={doc.source}
                        />
                        {doc.source && doc.source.includes("Email:") ? <span
                            style={{
                                cursor: 'pointer'
                            }}
                            title="Click to copy to clipboard."
                            onClick={() => {
                                let value = doc.source.split(": ")[1];
                                if (!value) return;
                                if (value.includes('<'))
                                    value = value.split('<')[1];
                                if (value.includes('>'))
                                    value = value.split('>')[0];
                                copyText(value);
                                toast.info("Copied to clipboard");
                            }}
                        >
                            {doc.source.split(": ")[1]}
                        </span> : doc.source === "Uploaded" ? <span
                            title={uploadedBy}
                        >
                            {uploadedBy}
                        </span> : doc.source}
                    </div>
                    </div>

                    <div className='meta-details-wrapper'>
                    <div className="meta-details-label">Received On</div>
                    <div className="meta-details-value">{formatDate(doc.receivedOn)}</div>

                    </div>
                   
                    <div className='meta-details-wrapper'>
                    <div className="meta-details-label">Last Modified</div>
                    <div className="meta-details-value">{formatDate(doc.lastModified)}</div>
                    </div>
                    

                   

                    {/* Document error? */}
                    {doc.job && !doc.job.success && !doc.job.working && doc.job.error ? <div
                        className="meta-automate-log-container"
                        style={{
                            borderLeft: '2px solid transparent',
                            marginLeft: 0,
                            paddingBottom: 0
                        }}
                    >
                        <div
                            className="meta-automate-log-error"
                            style={{
                                marginLeft: 0,
                                width: 'calc(100% - 80px)'
                            }}
                            onContextMenu= {(e) => this.openCopyDocErrorMenu(e, doc.job.error)}
                           
                        >
                        {/*Open context menu to copy value*/}
                        <ContextMenu
                            load={(open,close) => {
                                this.openCopyDocErrorMenu = open;
                                this.closeCopyDocErrorMenu = close;
                            }}
                            options={[
                                {
                                    label: 'Copy Value',
                                    action: (err) => {
                                        copyText(err);
                                        toast.info("Copied to clipboard");
                                    }
                                }
                            ]}
                        />
                            {doc.job.error}
                        </div>

                        <div
                            className="meta-error-clear"
                            onClick={this.clearDocumentError}
                        >Clear error</div>
                    </div> : <></>}
                </> :

                // Stack meta details
                <>
                    <h1>Details</h1>

                    {stack.path !== "___unset___" && !isPseudoStack && <div className="meta-details-wrapper">
                        <div className="meta-details-label">Stack name</div>
                        <div className="meta-details-value">
                            <Editable
                                className="meta-details-config"
                                containerClass="meta-details-config-container"
                                inputClass="meta-details-config-edit"
                                valueClass="meta-details-config-value"
                                fieldName="Stack name"
                                value={(() => {
                                    if (isPseudoStack) return stack.path;

                                    const pathCmp = stack.path.split("/");
                                    return pathCmp[pathCmp.length-1];
                                })()}
                                editable={!isPseudoStack}
                                setValue={(value) => {
                                    if (stack.path === value)
                                        return; // No change
                                    
                                    let pathCmp = stack.path.split("/");
                                    pathCmp[pathCmp.length-1] = value.replace(/\//g, ""); // Replace last bit...

                                    this.props.renameStackExec(stack, pathCmp.join("/"));
                                }}
                            />
                        </div>
                    </div>}

                    <div className="meta-details-wrapper">
                        <div className="meta-details-label">Total Files</div>
                        <div className="meta-details-value">{stack.numDocuments} {plural('document', stack.numDocuments)}</div>
                    </div>

                    <div className="meta-details-wrapper">
                        <div className="meta-details-label">Unread Files</div>
                        <div className="meta-details-value">{stack.numUnread} unread</div>
                    </div>

                    <div className="meta-details-wrapper">
                        <div className="meta-details-label">Last Modified</div>
                        <div className="meta-details-value">{formatDate(stack.lastModified)}</div>
                    </div>

                    {/* Admin only */}
                    {stack.baseName ? <div className="meta-details-wrapper">
                        <div className="meta-details-label">Base Stack</div>
                        <div className="meta-details-value">{stack.baseName.replace(/\//g, " › ")}</div>
                    </div> : ''}

                    {/* Only for regular stacks */}
                    {stack?._id?.length === 24 ? <>
                        {this.props.filter?.length ? <h1 style={{ position: 'relative' }}>
                            Filters
                            <div
                                className="meta-filters-clear"
                                onClick={() => this.props.setFilter([])}
                            >Clear all</div>
                        </h1> : ""}

                        {/* Filter documents */}
                        {this.props.filter?.map((f,idx) => <div
                            className="metadata-field-editable"
                            style={{ width: 'calc(100% - 4px)' }}
                            key={idx}
                        >
                            <div className="metadata-edit-key-select">
                                <CreatableSelect
                                    className="metadata-select-container"
                                    classNamePrefix="metadata-select"
                                    value={{
                                        value: f.key,
                                        label: f.key?.toUpperCase() ?? ""
                                    }}
                                    options={filterKeys.map(key => ({
                                        value: key,
                                        label: key.toUpperCase()
                                    })) ?? []}
                                    isSearchable={true}
                                    placeholder="Select field key"
                                    onChange={async (option) => {
                                        const pfilters = [...this.props.filter];
                                        if (pfilters[idx].key !== option.value) {
                                            pfilters[idx].value = "";
                                        }
                                        pfilters[idx].key = option.value;
                                        if (option.value === "🛑 confidence") {
                                            pfilters[idx].value = "90";
                                            const filters = { ...this.state.filters };
                                            filters[f.key] = "90";
                                            this.setState({ filters });
                                        }
                                        this.props.setFilter(pfilters);
                                    }}
                                />
                            </div>
                            : 
                            {/*Based on the value of filters[idx].key we will show the input field*/}
                            {["👩‍💻 assignee", "📤 uploaded by"].includes(f.key) ? <Select
                                className="metadata-select-container dropdown"
                                classNamePrefix="metadata-select"
                                value={(() => {
                                    if (!f.value)
                                        return {value: "none", label: "Select user..."} 
                                    
                                    let filterUser = team.users.find(u => u._id === f.value); 
                    
                                    if (!filterUser)
                                        return { value: "none", label: "Select user..."};
                    
                                    return {
                                        value: filterUser,
                                        label: filterUser.name
                                    };
                                })()}
                                options={[
                                    ...team.users.filter(u => u.name && u.name.length).map(u => ({
                                        value: u._id,
                                        label: u.name
                                    }))
                                ]}
                                isSearchable={true}
                                placeholder="Select user..."
                                onChange={async (option) => {
                                    if (option.value && option.value !== "none") {
                                        // const stateFilters = { ...this.state.filters };
                                        const pfilters = [...this.props.filter];
                                        if (pfilters[idx].key !== option.value) {
                                            pfilters[idx].value = "";
                                        }
                                        pfilters[f.key] = option.value;
                                        this.setState({ filters: pfilters });
                                        const filters = [...this.props.filter];
                                        filters[idx].value = option.value;
                                        this.props.setFilter(filters);
                                    }
                                }}
                            /> : 
                            f.key === "🚩 flagged" ? <Select
                                className="metadata-select-container dropdown"
                                classNamePrefix="metadata-select"
                                value={(() => {

                                })()}
                                options={["Yes", "No"].map(o => ({
                                    value: o,
                                    label: o
                                }))}
                                placeholder="Select status..."
                                onChange={async (option) => {
                                    if (["Yes", "No"].includes(option?.value ?? "")) {
                                       
                                            
                                            const pfilters = [...this.props.filter];
                                            if (pfilters[idx].key !== option.value) {
                                                pfilters[idx].value = "";
                                            }
                                            pfilters[f.key] = option.value;
                                            this.setState({ filters: pfilters });
                                        const filters = [...this.props.filter];
                                        filters[idx].value = option.value;
                                        this.props.setFilter(filters);
                                    }
                                }}
                            /> :
                            <>
                                <input
                                id={"filter-value-"+idx}
                                name={"filter-value-"+idx}
                                type="text"
                                className={"metadata-edit-value"}
                                value={this.state.filters[f.key] ?? ""}
                                placeholder={ f.key === "🛑 confidence" ? "90" : ""}
                                onChange={e => {
                                    const filters = { ...this.state.filters };
                                    filters[f.key] = e.target.value;
                                    this.setState({ filters });
                                }}
                                onKeyDown={(e) => inputKey(e, {
                                    "Enter": () => {
                                        const filters = [...this.props.filter];
                                        filters[idx].value = this.state.filters[f.key] ?? "";
                                        this.props.setFilter(filters);
                                    }
                                })}
                                />
                            </> 
                        }
                        <img
                        className="metadata-edit-delete"
                        src="https://static.stax.ai/images/icons/remove.png"
                        alt="(-)"
                        title="Clear filter"
                        onClick={() => {
                            // Clear filter and reload docs
                            const filters = [...this.props.filter];
                            filters.splice(idx,1);
                            this.props.setFilter(filters);
                        }}
                    />
                        </div>)}

                        <div style={{
                            clear: 'both',
                            height: 20
                        }}/>

                        <div
                            className="meta-action"
                            style={{
                                backgroundImage: "url('https://static.stax.ai/images/icons/action-filter.png')"
                            }}
                            onClick={() => this.props.setFilter([
                                ...this.props.filter,
                                {
                                    key: "",
                                    value: ""
                                }
                            ])}
                        >Add a new filter</div>
                    </> : ""}

                </>}

                {/* Timeline View */}
                {allowTimelineView && <>
                    <h1>Timeline</h1>
                    <p style={{ marginBottom: 10 }}>
                        This stack contains documents with medical events. <strong>Timeline View</strong> lets you see and edit these events in chronological order.
                    </p>
                    <div
                        className="meta-action"
                        style={{ backgroundImage: "url('https://static.stax.ai/images/icons/timeline.png')" }}
                        onClick={() => {
                            window.location.href = "/timeline/" + stack._id;
                        }}
                    >Open Timeline View</div>
                </>}

                {/* Sharing settings */}
                {sel.length === 0 && !isPseudoStack && <>
                    <h1>Sharing</h1>
                    <img
                        alt="Share"
                        className="meta-share-icon"
                        src={"https://static.stax.ai/images/icons/" + (stack.restrict ? "admin" : "team") + ".png"}
                        title={stack.restrict ? "All Team Admins" : "All Team Members"}
                    />
                    {stack.restrict && stack.access && stack.access.length === 0 ? <div className="meta-sharing-info">Only admins have access</div> : ""}
                    {!stack.restrict ? <div className="meta-sharing-info">Entire team has access</div> : ""}
                    {stack.restrict && stack.access.map((userId,idx) => {

                        const user = team.users.find(u => u._id === userId);

                        if (!user)
                            return "";

                        return (<div
                            key={idx}
                            className={"meta-share-icon palette-color-" + String(idx%8)}
                            title={user.name}
                        >{user.name.substring(0,1)}</div>);
                    })}

                        <div
                            className="meta-share-settings"
                            onClick={this.openSharing}
                            onContextMenu={this.openSharing}
                        />
                        <ContextMenu
                            load={(open,close) => {
                                this.openSharing = open;
                                this.closeSharing = close;
                            }}
                            options={[
                                {
                                    label: 'Share with entire team',
                                    action: () => this.props.updateStack(stack._id, {
                                        restrict: false
                                    })
                                },
                                {
                                    label: 'Restrict to admins only',
                                    action: () => this.props.updateStack(stack._id, {
                                        restrict: true,
                                        access: []
                                    })
                                },
                                {
                                    label: 'Advanced sharing settings',
                                    action: () => this.props.manageSharing()
                                }
                            ]}
                        />

                        <h1>Sorting</h1>
                        <img
                            alt="Sorting"
                            className="meta-share-icon"
                            src={"https://static.stax.ai/images/icons/" + (!stack.enabled ? "disabled" : stack.rules && stack.rules.enabled ? "rules" : "auto") + ".png"}
                            title={!stack.enabled ? "Disable sorting into stack" : stack.rules && stack.rules.enabled ? "Sort by predefined rules" : "Sort automatically"}
                            style={{
                                borderRadius: 0
                            }}
                        />
                        <div className="meta-sharing-info">{!stack.enabled ? "Don't sort files into this stack" : stack.rules && stack.rules.enabled ? "Sort using predefined rules" : "Sort automatically by examples"}</div>

                        <div
                            className="meta-share-settings"
                            onClick={this.openSorting}
                            onContextMenu={this.openSorting}
                        />
                        
                        <ContextMenu
                            load={(open,close) => {
                                this.openSorting = open;
                                this.closeSorting = close;
                            }}
                            options={[
                                {
                                    label: 'Sort automatically by examples',
                                    action: () => this.props.updateStack(stack._id, {
                                        enabled: true,
                                        rules: {
                                            ...stack.rules,
                                            enabled: false
                                        }
                                    })
                                },
                                {
                                    label: 'Disable sorting into this stack',
                                    action: () => this.props.updateStack(stack._id, {
                                        enabled: false
                                    })
                                },
                                {
                                    label: 'Advanced sorting settings',
                                    action: () => this.props.manageSorting()
                                }
                            ]}
                        />

                        {/* Automation modules */}
                        <h1 title="Use Automation modules to extract specific data or trigger actions on other apps.">Automate</h1>

                        {/* Stack */}
                        <div
                            className="meta-automate-pipeline-step"
                            style={{ backgroundImage: "url('https://static.stax.ai/images/icons/" + (this.props.user && this.props.user.enableDarkMode ? "dark/" : "") + "folder.png')" }}
                        >Document received in stack</div>
                        <div className="meta-automate-pipeline-pipe" />

                        {/* Intermediary steps */}
                        {(stack.pipeline ? stack.pipeline : []).map((step,idx) => {
                            const module = this.props.modules.find(m => m._id === step.module);
                            if (!module)
                                return (<div key={idx}></div>);

                            return (<div key={idx} style={{ position: 'relative' }}>
                                <div
                                    className="meta-automate-pipeline-step meta-automate-pipeline-step-custom"
                                    style={{ backgroundImage: "url('" + module.icon + "')" }}
                                >
                                    {module.name}
                                    <br />
                                    {(module.name !== "Execute Custom Python Script" || ADMIN_EMAILS.includes(this.props.user?.email)) && 
                                        <div
                                            className="meta-automate-pipeline-step-edit"
                                            onClick={() => {
                                                this.props.editModuleConfig(stack, idx, module)}}
                                            title="Edit the module configuration"
                                        >Edit</div>
                                    }
                                    {(module.trainer && (!step.trainer || !step.trainer.training)) && <div
                                        className="meta-automate-pipeline-step-edit"
                                        onClick={() => {
                                            this.props.trainModule(stack, module, step);
                                            this.setState({ trainingOverride: {
                                                ...this.state.trainingOverride,
                                                [step._id]: true
                                            } });
                                        }}
                                        title="Train module with labeled documents"
                                    >{step.trainer.data ? "Retrain" : "Train"}</div>}
                                    {!step.locked && <div
                                        className="meta-automate-pipeline-step-detach"
                                        onClick={() => this.props.modal("confirm", {
                                            message: <>
                                                If you detach this module, you will <strong>permanently lose the saved pipeline configuration</strong>. Documents moved into this stack will also no longer perform this action.
                                            </>,
                                            action: () => this.props.detachModule(stack, module, step)
                                        })}
                                        title="Delete module from pipeline"
                                    >Delete</div>}
                                
                                    </div>
                                {step.trainer && step.trainer.training && <div
                                    className="meta-automate-pipeline-step-training"
                                    title={"Training since: " + formatDate(step.trainer.lastTrained)}
                                    style={{ animation: "spin 1.5s linear infinite" }}
                                />}
                                <div
                                    className="meta-automate-pipeline-pipe"
                                    style={{
                                        height: module.trainer && step.trainer ? 20 : undefined
                                    }}
                                />
                                {module.trainer && step.trainer ? <div className="meta-automate-pipeline-training" >
                                    {(this.state.trainingOverride[step._id] || step.trainer.training) ? <>
                                        Training in progress...
                                    </> : step.trainer.data && step.trainer.lastTrained ? <>
                                        Last trained on <strong>{formatDate(step.trainer.lastTrained)}</strong>
                                    </> : <>
                                        <span style={{ float: 'left', marginRight: 10 }}>Not yet trained.</span>
                                        <div
                                            className="meta-automate-pipeline-step-edit"
                                            style={{ color: "#ff4352" }}
                                            onClick={() => {
                                                this.props.trainModule(stack, module, step);
                                                this.setState({ trainingOverride: {
                                                    ...this.state.trainingOverride,
                                                    [step._id]: true
                                                } });
                                            }}
                                            title="Train module with labeled documents"
                                        >Train</div>
                                    </>}
                                </div> : ""}
                            </div>);
                        })}

                        {/* Add module */}
                        <div
                            className="meta-automate-pipeline-step meta-automate-pipeline-add"
                            style={{ backgroundImage: "url('https://static.stax.ai/images/icons/" + (user && user.enableDarkMode ? "dark/" : "") + "create.png')" }}
                            title="Click to configure and attach module to stack"
                            onClick={() => window.location.href = '/settings/automate/' + stack._id}
                        >Attach an automation step</div>

                        <div style={{ clear: 'both', height: 20 }} />
                        <div
                            className="meta-action meta-toggle-advanced"
                            style={{ backgroundImage: "url('https://static.stax.ai/images/icons/section-" + (this.state.showAdvanced ? "collapse" : "expand") + ".png" }}
                            onClick={() => this.setState({ showAdvanced: !this.state.showAdvanced })}
                        >{this.state.showAdvanced ? "Hide advanced options" : "Show advanced options"}</div>

                        {this.state.showAdvanced ? <>

                            <h1>Forms</h1>

                            <p>
                                Allow users to add or edit <strong>Fields</strong> for documents in this stack using pre-defined forms.
                            </p>

                            {!this.props.forms || this.props.forms.length === 0 ? <p>
                                <br />
                                Your team does not have any forms.<br />
                                Go to <a href="/settings/forms">Manage Team Settings → Forms</a> to create or edit forms.
                            </p> : <>
                                {/* Attached forms */}
                                {stack.forms.map((formId,idx) => {
                                    const form = this.props.forms.find(f => f._id === formId);
                                    if (!form)
                                        return <></>;

                                    return <div
                                        className="meta-form-container"
                                        title={form.title}
                                        key={idx}
                                    >
                                        {form.title}
                                        <div
                                            className="meta-form-detach"
                                            title="Detach form from stack"
                                            onClick={() => {
                                                let forms = [ ...stack.forms ];
                                                forms.splice(idx,1);
                                                this.props.updateStack(stack._id, { forms });
                                            }}
                                        />
                                    </div>;
                                })}

                                {/* Attach new form? */}
                                {this.props.forms.filter(f => !stack.forms.includes(f._id)).length > 0 ? <>
                                    <br />
                                    <div className="meta-details-label meta-details-label-select">Attach form</div>
                                    <Select
                                        className="select-container"
                                        classNamePrefix="select"
                                        value={null}
                                        options={this.props.forms.filter(f => !stack.forms.includes(f._id)).map(f => ({
                                            value: f._id,
                                            label: f.title
                                        }))}
                                        isSearchable={true}
                                        placeholder="Select form..."
                                        onChange={async (option) => this.props.updateStack(stack._id, {
                                            forms: [
                                                ...stack.forms,
                                                option.value
                                            ]
                                        })}
                                    />
                                </> : ""}
                            </>}
                        </> : <></>}
                </>}

                {/* Other single document meta */}
                {sel.length === 1 && doc && doc._id && <>
                    {this.renderDocFlags(doc)}
                    {this.renderDocSync(user, team, doc)}
                    {this.renderDocSharing(user, team, doc, stack)}
                    {this.renderDocForms(user, team, doc, forms)}
                    {this.renderDocStatement(doc, this.props.statement)}
                    {this.renderDocFields(user, team, doc, stack)}
                    {this.renderDocAttachments(user, team, doc, stack)}
                    {this.renderDocTables(user, team, doc, stack)}
                    {this.renderSplits(user, team, doc, stack)}
                    {this.renderDocDuplicates(user, team, doc, stack)}
                    {this.renderDocAutomate(user, team, doc, stack)}
                    {this.renderDocHistory(user, team, doc, stack)}
                    {this.renderDocFillable(user, team, doc, stack)}
                </>}
                
                {/* Meta actions */}
                {actions.length > 0 && <h1>Actions</h1>}

                {/* Clear flag? */}
                {sel.length === 1 && doc && doc.flagged && <div
                        className="meta-action"
                        title="Clear flag on document"
                        style={{
                            backgroundImage: "url('https://static.stax.ai/images/icons/action-check.png",
                            marginTop: 3
                        }}
                        onClick={() => this.props.updateDocument(doc._id, { flagged: false })}
                    >Unflag document</div>}

                {/* Remaining actions */}
                {actions.map((action,idx) => <div
                    className="meta-action"
                    key={idx}
                    title={action.tooltip}
                    style={{ backgroundImage: "url('https://static.stax.ai/images/icons/" + action.icon }}
                    onClick={() => action.action ? action.action(sel, doc) : null}
                >{typeof action.title === "function" ? action.title(sel, doc) : action.title}</div>)}

            </div>

        </div>);
    }

};